// ============================================
// OWA — i18n dictionary
// Languages: EN · ES · IT · DE · NO · NL
// ============================================

export const LANGUAGES = [
  { code: 'en', label: 'English',  flag: '🇬🇧', locale: 'en-GB' },
  { code: 'es', label: 'Español',  flag: '🇪🇸', locale: 'es-ES' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹', locale: 'it-IT' },
  { code: 'de', label: 'Deutsch',  flag: '🇩🇪', locale: 'de-DE' },
  { code: 'no', label: 'Norsk',    flag: '🇳🇴', locale: 'nb-NO' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱', locale: 'nl-NL' },
];

export const DEFAULT_LANGUAGE = 'en';

export const translations = {
  // ══════════════════════════════════════════
  en: {
    cookies: { text: 'We use cookies to analyse traffic and improve your experience.', policy: 'Privacy policy', accept: 'Accept', reject: 'Reject' },
    nav: {
      home: 'Home',
      activities: 'Activities',
      destinations: 'Destinations',
      blog: 'Blog',
      about: 'About',
      contact: 'Contact',
      language: 'Language',
      currency: 'Currency',
      dayMode: 'Day mode',
      nightMode: 'Night mode',
    },
    hero: {
      eyebrow: 'ORIGIN WILD ADVENTURE',
      title: 'One Wild Adventure',
      subtitle: 'Not for everyone.',
      description: 'Designed for those who seek more than a typical tour. Discover the Canary Islands by sea, air and land.',
      island: 'Island',
      allIslands: 'All islands',
      searchDestination: 'Destination',
      searchPlaceholder: 'Where are we going?',
      searchFrom: 'From',
      searchTo: 'To',
      searchAddDate: 'Add date',
      searchButton: 'Search',
      searchActivity: 'Activity or place',
      searchActivityPlaceholder: 'What adventure are you after?',
      searchDate: 'Date',
      searchAnyDate: 'Any date',
      searchParticipants: 'Participants',
      searchPeople: 'people',
    },
    trust: {
      freeCancellation: 'Free cancellation 24h',
      deposit: 'Book with just 20%',
      localGuides: 'Certified local guides',
    },
    promise: {
      fairPricingTitle: 'Fair, transparent pricing',
      fairPricingText: 'The price you see is the price you pay — no hidden fees, no surprises.',
      clearCancellationTitle: 'Clear cancellation',
      clearCancellationText: 'Every activity shows its cancellation policy before you book.',
      directSupportTitle: 'Direct support',
      directSupportText: 'We advise you and confirm availability instantly on WhatsApp.',
    },
    destinations: {
      eyebrow: 'Explore',
      title: 'The best destinations',
      titleAccent: 'in the Canaries',
      viewAll: 'View all destinations',
      activityCount: 'activities',
      activityCountOne: 'activity',
    },
    inspired: {
      eyebrow: 'Inspiration',
      title: 'Get inspired for your next trip',
      viewAll: 'View all activities',
    },
    card: {
      featured: 'Featured',
      from: 'From',
      reviews: 'reviews',
      reviewsOne: 'review',
      noReviews: 'New',
      bookNow: 'Book now',
      viewDetails: 'View details',
      perPerson: 'per person',
    },
    why: {
      eyebrow: 'Where adventure begins',
      title: 'Not for everyone.',
      titleAccent: 'Only for explorers.',
      description: 'OWA was born from a passion for the wild. We do not sell tours: we craft tailored adventures by sea, air and land across the most spectacular islands of the Atlantic.',
      statCustomers: 'Happy guests',
      statActivities: 'Activities',
      statIslands: 'Islands covered',
      statRating: 'Average rating',
      feature1Title: 'Local guides',
      feature1Text: 'Our team are Canary-born and know every corner of the islands.',
      feature2Title: 'Premium adventures',
      feature2Text: 'Experiences curated down to the detail, not generic agency tours.',
      feature3Title: 'Small groups',
      feature3Text: 'Maximum 8 people per activity. Closer, more authentic.',
      feature4Title: 'Secure booking',
      feature4Text: 'Free cancellation up to 24 hours before. 100% secure payment.',
    },
    bestOf: {
      eyebrow: 'The best of the Canaries',
      title: 'Best of Canary',
      description: 'Activities carefully selected by our local guides. Filter by category and find your next adventure.',
      filterAll: 'All',
      filterWater: 'Water Activities',
      filterDiving: 'Diving & Snorkel',
      filterLand: 'Adventure & Land',
      filterAir: 'Air Sports',
      filterNature: 'Nature & Wildlife',
      filterFood: 'Food & Drink',
    },
    categoryChips: {
      barco: 'Boat',
      delfines: 'Dolphins',
      jetSki: 'Jet ski',
      buggy: 'Buggy',
      buceo: 'Diving',
      familias: 'Families',
    },
    testimonials: {
      eyebrow: 'Testimonials',
      title: 'Real stories from explorers',
      empty: 'Be the first to share your experience.',
    },
    blog: {
      eyebrow: 'Blog',
      title: 'Stories, tips, & guides',
      viewAll: 'View all articles',
      readMore: 'Read more',
      minRead: 'min read',
    },
    socialFeed: {
      eyebrow: 'Follow us',
      title: 'Live from the islands',
      subtitle: 'Real moments from our adventures on Instagram and TikTok.',
    },
    blogPage: {
      heading: 'Stories, tips & guides',
      subtitle: 'Inspiration, tips and guides to discover the Canary Islands like a local.',
      topics: 'Topics',
      all: 'All',
    },
    blogDetail: {
      notFound: 'Article not found',
      notFoundText: 'The post you are looking for does not exist or has been removed.',
      back: 'Back to blog',
      author: 'OWA Team',
      keepReading: 'Keep reading',
      noContent: 'Content not available.',
      ctaTitle: 'Ready to meet the giants of the Atlantic?',
      ctaText: 'Come aboard the Ocean Giants Cruise and experience pilot whales, sperm whales and dolphins with a marine-biologist guide, a hydrophone and a swim in a secret cove.',
      ctaBook: 'Book the Ocean Giants Cruise',
      ctaAsk: 'Ask us anything',
    },
    newsletter: {
      title: 'Exclusive offers for wild explorers',
      description: 'Subscribe and receive secret discounts and the best adventures every week.',
      placeholder: 'Your email address',
      button: 'Subscribe',
      disclaimer: 'No spam. Unsubscribe at any time.',
      success: 'Thanks for subscribing.',
      error: 'Something went wrong. Please try again.',
    },
    wishlist: {
      eyebrow: 'Your selection',
      title: 'Favourites',
      subtitle: 'Save the adventures you love and find them here.',
      countSuffix: 'saved adventures',
      clearAll: 'Clear all',
      emptyTitle: 'You have no favourites yet',
      emptyBody: 'Tap the heart on any adventure to save it here.',
      exploreCta: 'Explore adventures',
      add: 'Add to favourites',
      remove: 'Remove from favourites',
    },
    pagination: {
      label: 'Pagination',
      perPage: 'Show:',
      prev: 'Previous page',
      next: 'Next page',
    },
    footer: {
      description: 'Origin Wild Adventure. Designed for those who seek more than a conventional tour in the Canary Islands.',
      company: 'Company',
      about: 'About us',
      blog: 'Blog',
      careers: 'Work with us',
      press: 'Press',
      affiliates: 'Affiliates',
      help: 'Help',
      helpCenter: 'Help centre',
      cancellation: 'Cancellation policy',
      safety: 'Safety & trust',
      terms: 'Terms & conditions',
      privacy: 'Privacy policy',
      contact: 'Contact',
      rights: 'All rights reserved.',
      madeWith: 'Made with care in the Canary Islands',
      taxNote: 'All prices include IGIC (Canary Islands tax).',
    },
    activity: {
      overview: 'Overview',
      included: 'Included',
      excluded: 'Not included',
      itinerary: 'Itinerary',
      meetingPoint: 'Meeting point',
      reviews: 'Reviews',
      faq: 'FAQs',
      highlights: 'Highlights',
      duration: 'Duration',
      groupSize: 'Group size',
      smallGroupDefault: 'Small group',
      pickupDefault: 'We will confirm the exact pickup point and conditions after booking.',
      languages: 'Languages',
      difficulty: 'Difficulty',
      minAge: 'Minimum age',
      cancellationPolicy: 'Cancellation',
      whatToBring: 'What to bring',
      selectDate: 'Select date',
      adults: 'Adults',
      children: 'Children',
      infants: 'Infants',
      total: 'Total',
      checkAvailability: 'Check availability',
      relatedTitle: 'You might also like',
    },
    activitiesPage: {
      eyebrow: 'Activities',
      headingPre: 'All ',
      headingEm: 'adventures',
      subtitle: 'Find your next experience among more than 120 curated activities.',
      filters: 'Filters',
      hide: 'Hide filters',
      searchPlaceholder: 'Search…',
      destinations: 'Destinations',
      categories: 'Categories',
      price: 'Price',
      resultsFound: 'activities found',
      perPage: 'Show:',
      sort: 'Sort:',
      sortFeatured: 'Featured',
      sortPriceAsc: 'Price: low to high',
      sortPriceDesc: 'Price: high to low',
      sortRating: 'Top rated',
      viewList: 'List view',
      viewGrid: 'Grid view',
      loadError: 'We could not load the activities right now. Please try again in a few minutes.',
      empty: 'No activities match these filters.',
    },
    activityDetail: {
      back: 'Back to activities',
      notFound: 'Activity not found.',
      share: 'Share activity',
      save: 'Save activity',
      newActivity: 'New activity on OWA',
      verified: 'Verified local operator',
      onRequest: 'Booking on request',
      prevImage: 'Previous image',
      nextImage: 'Next image',
      tabDesc: 'Description',
      tabIncluded: 'What\'s included',
      tabMeeting: 'Meeting point',
      tabReviews: 'Reviews',
      about: 'About the experience',
      group: 'Group',
      insurance: 'Insurance',
      video: 'Watch the experience',
      cancelTitle: 'Cancellation policy',
      requirements: 'Important requirements',
      faqDuration: 'How long does this activity last?',
      faqIncluded: 'What does this activity include?',
      faqNotIncluded: 'What is not included?',
      faqMeetingPoint: 'Where is the meeting point?',
      faqCancellation: 'What is the cancellation policy?',
      faqRequirements: 'Are there any requirements or restrictions?',
      notIncludedH: 'Not included',
      whereMeet: 'Where we meet',
      ratingOf5: 'out of 5',
      reviewsRefNote: 'reference ratings. We will publish our own verified reviews once there are direct bookings on OWA.',
      newReviewsNote: 'We will publish verified reviews once the first bookings are completed.',
      dateLabel: 'Date',
      timeLabel: 'Time',
            couponPlaceholder: "Discount code",
            addonsTitle: "Optional extras",
            addonPerPerson: "per person",
            couponApply: "Apply",
            couponDiscount: "Discount",
            couponInvalid: "Invalid coupon.",
            dateUnavailable: "This date is not available.",
            soldOut: "Sold out",
            seatsLeft: "{count} left",
      fullName: 'Full name',
      phone: 'Phone',
      each: 'each',
      acceptPolicy: 'I accept this activity’s policy:',
      payingSumup: 'Pay securely with SumUp',
      openingSumup: 'Opening SumUp…',
      payPrep: 'Online payment coming soon',
      sendingRequest: 'Sending request…',
      requestBooking: 'Request booking',
      confirmWhatsApp: 'Confirm availability on WhatsApp',
      whatsappDoubts: 'Questions? Message us on WhatsApp',
      payTotalEur: 'Full payment in EUR',
      checkoutNoteReady: 'Checkout is already integrated. The secure backend and real credentials still need to go live.',
      checkoutNoteRequest: 'We will contact you to confirm availability and final conditions.',
      amountEurNote: 'The final amount is processed in EUR at the activity’s official price.',
      payNowDeposit: 'Pay now (deposit)',
      balanceOnBoard: 'Balance paid on board',
      tSelDateT: 'Select a date',
      tSelDateD: 'Please choose the day for your activity.',
      tSelTimeT: 'Select a time',
      tSelTimeD: 'Choose one of the available time slots.',
      tSelPaxT: 'Select participants',
      tSelPaxD: 'Add at least one place before continuing.',
      tDataT: 'Complete your details',
      tDataD: 'We need your name, email and phone to manage the booking.',
      tAcceptT: 'Accept the cancellation',
      tAcceptD: 'You must accept this activity’s specific policy.',
      tSentT: 'Request sent',
      tSentD: 'We will contact you very soon to confirm availability and final conditions.',
      tSentFailT: 'Could not send the request',
      tPayFailT: 'Could not start the payment',
      tryAgain: 'Please try again in a few minutes.',
    },
    places: {
      eyebrow: 'Destinations',
      headingPre: 'Canary ',
      headingEm: 'Islands',
      subtitle: 'Eight volcanic islands, golden beaches, clear skies and endless activities.',
      loadError: 'We could not load the destinations right now. Please try again in a few minutes.',
      empty: 'No destinations published yet.',
      canaries: 'Canary Islands',
      activitiesAvailable: 'activities available',
      comingSoon: 'Coming soon',
    },
    notFound: {
      title: 'Page not found',
      description: "The page you're looking for doesn't exist or has moved.",
      home: 'Back to home',
      activities: 'Browse activities',

      islandNotFound: 'Island not found.',
      backDest: 'All destinations',
      experiencesIn: 'Experiences in',
      viewCatalog: 'View in the catalogue',
      preparingTitle: 'We are still preparing',
      preparingText: 'There are no experiences published on this island yet. Tell us what you are after and we will put together a personalised proposal.',
      whatsapp: 'Ask on WhatsApp',
      contactUs: 'Contact us',
      catNotFound: 'Category not found.',
      backActivities: 'All activities',
      experiences: 'experiences',
      soonTitle: 'More experiences soon',
      soonText: 'We are expanding this category. In the meantime, explore all activities.',
      viewCatalogBtn: 'View the catalogue',
    },
    about: {
      eyebrow: 'About OWA',
      headingPre: 'Adventures designed ',
      headingEm: 'for explorers',
      intro: 'OWA — Origin Wild Adventure was born in 2018 in Gran Canaria with a clear goal: to offer authentic, sustainable and memorable experiences in the Canary Islands. No crowded tours. Just small groups, local guides and the best locations.',
      yearsBig: '7+ years',
      yearsSmall: 'creating adventures',
      valuesEyebrow: 'Our values',
      valuesHeadingPre: 'What drives ',
      valuesHeadingEm: 'us',
      v1Title: 'Authenticity',
      v1Text: 'Real routes and experiences, away from the crowds.',
      v2Title: 'Passion',
      v2Text: 'Every adventure is created with love for detail.',
      v3Title: 'Sustainability',
      v3Text: 'We look after the islands for the next generations.',
      v4Title: 'Community',
      v4Text: 'We work with 100% local Canarian suppliers.',
    },
    contact: {
      eyebrow: 'Contact',
      headingPre: 'We are here ',
      headingEm: 'for you',
      subtitle: 'Tell us what adventure you have in mind and we will help you make it happen.',
      office: 'Head office',
      writeUs: 'Write to us',
      formIntro: 'Fill in the form and our team will get in touch with you as soon as possible.',
      name: 'Name',
      subject: 'Subject',
      messagePlaceholder: 'Tell us which adventure you are interested in…',
      send: 'Send message',
      sentTitle: 'Message sent!',
      sentText: 'We will reply within 24 hours.',
    },
    auth: {
      sessionExpired: "Your session has expired. Please sign in again.",
            orContinue: "Or continue with",
            continueFacebook: "Continue with Facebook",
            loginEyebrow: 'Client area',
      loginTitle: 'Access your account',
      loginDesc: 'Log in to view your bookings and manage your profile.',
      password: 'Password',
      forgot: 'Recover access',
      loggingIn: 'Signing in…',
      login: 'Sign in',
      noAccount: 'Don\'t have an account yet?',
      createAccount: 'Create account',
      loginError: 'Could not sign in.',
      passwordPlaceholder: 'Your password',
      benefitsTitle: 'Your OWA account',
      benefit1: 'Check and manage your bookings',
      benefit2: 'Save your favourite activities',
      benefit3: 'Get exclusive offers for explorers',
      regEyebrow: 'New user',
      regTitle: 'Create your account',
      regDesc: 'Sign up to book faster and save your favourite adventures.',
      fullName: 'Full name',
      fullNamePlaceholder: 'First and last name',
      phone: 'Phone',
      passwordMin: 'At least 8 characters',
      repeatPassword: 'Repeat password',
      repeatPasswordPlaceholder: 'Repeat the password',
      haveAccount: 'I already have an account',
      creatingAccount: 'Creating account…',
      passwordMismatch: 'The passwords do not match.',
      registerError: 'Could not create the account.',
      accountEyebrow: 'My account',
      noSessionTitle: 'No active session',
      noSessionDesc: 'Sign in to view your bookings and manage your profile.',
      loginBtn: 'Sign in',
      privateEyebrow: 'Private area',
      hello: 'Hello,',
      myAccount: 'My account',
      accountDesc: 'A summary of your profile and your bookings.',
      logout: 'Log out',
      loggedOut: 'Signed out successfully.',
      profile: 'Profile',
      profileSub: 'Details for managing your bookings.',
      role: 'Role',
      company: 'Company',
      directCustomer: 'Direct customer',
      notProvided: 'Not provided',
      myBookings: 'My bookings',
      bookingsSub: 'Your history and upcoming trips.',
      updating: 'Updating',
      bookingsError: 'Could not load the bookings.',
      noBookingsTitle: 'No bookings yet',
      noBookingsText: 'Once you make your first booking, you will see your upcoming activities, amounts and statuses here.',
      reference: 'Reference',
      datePending: 'Date pending',
      amountPending: 'Amount pending',
      stConfirmed: 'Confirmed',
      stPaymentPending: 'Payment pending',
      stPaymentFailed: 'Payment failed',
      stPending: 'Pending',
      stCancelled: 'Cancelled',
    },
    common: {
      loading: 'Loading…',
      error: 'Something went wrong.',
      retry: 'Try again',
      close: 'Close',
      back: 'Back',
      next: 'Next',
      previous: 'Previous',
      showMore: 'Show more',
      showLess: 'Show less',
      needHelp: 'Need help?',
    },
    whatsapp: {
      label: 'Need help?',
      aria: 'Contact us on WhatsApp',
      prefill: "Hi OWA, I'd like information about an experience in the Canary Islands.",
    },
  },

  // ══════════════════════════════════════════
  es: {
    cookies: { text: 'Usamos cookies para analizar el tráfico y mejorar tu experiencia.', policy: 'Política de privacidad', accept: 'Aceptar', reject: 'Rechazar' },
    nav: {
      home: 'Inicio',
      activities: 'Actividades',
      destinations: 'Destinos',
      blog: 'Blog',
      about: 'Nosotros',
      contact: 'Contacto',
      language: 'Idioma',
      currency: 'Moneda',
      dayMode: 'Modo día',
      nightMode: 'Modo noche',
    },
    hero: {
      eyebrow: 'ORIGIN WILD ADVENTURE',
      title: 'One Wild Adventure',
      subtitle: 'No es para todos.',
      description: 'Diseñada para quienes buscan más que un tour típico. Descubre las Islas Canarias por mar, aire y tierra.',
      island: 'Isla',
      allIslands: 'Todas las islas',
      searchDestination: 'Destino',
      searchPlaceholder: '¿A dónde vamos?',
      searchFrom: 'Ida',
      searchTo: 'Vuelta',
      searchAddDate: 'Añadir fecha',
      searchButton: 'Buscar',
      searchActivity: 'Actividad o lugar',
      searchActivityPlaceholder: '¿Qué aventura quieres vivir?',
      searchDate: 'Fecha',
      searchAnyDate: 'Cualquier fecha',
      searchParticipants: 'Participantes',
      searchPeople: 'personas',
    },
    trust: {
      freeCancellation: 'Cancelación gratis 24h',
      deposit: 'Reserva con solo 20%',
      localGuides: 'Guías locales certificados',
    },
    promise: {
      fairPricingTitle: 'Precio justo y transparente',
      fairPricingText: 'El precio que ves es el que pagas, sin cargos ocultos ni sorpresas.',
      clearCancellationTitle: 'Cancelación clara',
      clearCancellationText: 'Cada actividad muestra su política de cancelación antes de reservar.',
      directSupportTitle: 'Atención directa',
      directSupportText: 'Te asesoramos y confirmamos disponibilidad al momento por WhatsApp.',
    },
    destinations: {
      eyebrow: 'Explora',
      title: 'Los mejores destinos',
      titleAccent: 'de Canarias',
      viewAll: 'Ver todos los destinos',
      activityCount: 'actividades',
      activityCountOne: 'actividad',
    },
    inspired: {
      eyebrow: 'Inspiración',
      title: 'Inspírate para tu próximo viaje',
      viewAll: 'Ver todas las actividades',
    },
    card: {
      featured: 'Destacado',
      from: 'Desde',
      reviews: 'reseñas',
      reviewsOne: 'reseña',
      noReviews: 'Nuevo',
      bookNow: 'Reservar',
      viewDetails: 'Ver detalles',
      perPerson: 'por persona',
    },
    why: {
      eyebrow: 'Donde empieza la aventura',
      title: 'No para todos.',
      titleAccent: 'Solo para exploradores.',
      description: 'OWA nace de la pasión por lo salvaje. No vendemos tours: creamos aventuras a medida por mar, aire y tierra en las islas más espectaculares del Atlántico.',
      statCustomers: 'Clientes felices',
      statActivities: 'Actividades',
      statIslands: 'Islas cubiertas',
      statRating: 'Valoración media',
      feature1Title: 'Guías locales',
      feature1Text: 'Nuestro equipo son canarios de nacimiento que conocen cada rincón de las islas.',
      feature2Title: 'Aventuras premium',
      feature2Text: 'Experiencias cuidadas al detalle, no tours de agencia genéricos.',
      feature3Title: 'Grupos pequeños',
      feature3Text: 'Máximo 8 personas por actividad. Más cerca, más auténtico.',
      feature4Title: 'Reserva segura',
      feature4Text: 'Cancelación gratuita hasta 24 horas antes. Pago 100% seguro.',
    },
    bestOf: {
      eyebrow: 'Lo mejor de Canarias',
      title: 'Best of Canary',
      description: 'Actividades cuidadosamente seleccionadas por nuestros guías locales. Filtra por categoría y encuentra tu próxima aventura.',
      filterAll: 'Todas',
      filterWater: 'Actividades Náuticas',
      filterDiving: 'Buceo & Snorkel',
      filterLand: 'Aventura & Tierra',
      filterAir: 'Deportes Aéreos',
      filterNature: 'Naturaleza & Fauna',
      filterFood: 'Gastronomía',
    },
    categoryChips: {
      barco: 'Barco',
      delfines: 'Delfines',
      jetSki: 'Jet ski',
      buggy: 'Buggy',
      buceo: 'Buceo',
      familias: 'Familias',
    },
    testimonials: {
      eyebrow: 'Testimonios',
      title: 'Historias reales de exploradores',
      empty: 'Sé el primero en compartir tu experiencia.',
    },
    blog: {
      eyebrow: 'Blog',
      title: 'Historias, consejos y guías',
      viewAll: 'Ver todos los artículos',
      readMore: 'Leer más',
      minRead: 'min de lectura',
    },
    socialFeed: {
      eyebrow: 'Síguenos',
      title: 'En directo desde las islas',
      subtitle: 'Momentos reales de nuestras aventuras en Instagram y TikTok.',
    },
    blogPage: {
      heading: 'Historias, consejos y guías',
      subtitle: 'Inspiración, consejos y guías para descubrir las Islas Canarias como un local.',
      topics: 'Temas',
      all: 'Todos',
    },
    blogDetail: {
      notFound: 'Artículo no encontrado',
      notFoundText: 'El post que buscas no existe o ha sido eliminado.',
      back: 'Volver al blog',
      author: 'Equipo OWA',
      keepReading: 'Sigue leyendo',
      noContent: 'Contenido no disponible.',
      ctaTitle: '¿Listo para conocer a los gigantes del Atlántico?',
      ctaText: 'Sube a bordo del Ocean Giants Cruise y vive el avistamiento de calderones, cachalotes y delfines con guía biólogo marino, hidrófono y baño en cala secreta.',
      ctaBook: 'Reservar Ocean Giants Cruise',
      ctaAsk: 'Preguntarnos lo que quieras',
    },
    newsletter: {
      title: 'Ofertas exclusivas para wild explorers',
      description: 'Suscríbete y recibe descuentos secretos y las mejores aventuras cada semana.',
      placeholder: 'Tu correo electrónico',
      button: 'Suscribirme',
      disclaimer: 'Sin spam. Puedes darte de baja en cualquier momento.',
      success: 'Gracias por suscribirte.',
      error: 'Algo ha fallado. Inténtalo de nuevo.',
    },
    wishlist: {
      eyebrow: 'Tu selección',
      title: 'Favoritos',
      subtitle: 'Guarda las aventuras que te gustan y encuéntralas aquí.',
      countSuffix: 'aventuras guardadas',
      clearAll: 'Vaciar todo',
      emptyTitle: 'Aún no tienes favoritos',
      emptyBody: 'Pulsa el corazón en cualquier aventura para guardarla aquí.',
      exploreCta: 'Explorar aventuras',
      add: 'Añadir a favoritos',
      remove: 'Quitar de favoritos',
    },
    pagination: {
      label: 'Paginación',
      perPage: 'Mostrar:',
      prev: 'Página anterior',
      next: 'Página siguiente',
    },
    footer: {
      description: 'Origin Wild Adventure. Diseñada para quienes buscan más que un tour convencional en las Islas Canarias.',
      company: 'Compañía',
      about: 'Sobre nosotros',
      blog: 'Blog',
      careers: 'Trabaja con nosotros',
      press: 'Prensa',
      affiliates: 'Afiliados',
      help: 'Ayuda',
      helpCenter: 'Centro de ayuda',
      cancellation: 'Política de cancelación',
      safety: 'Seguridad y confianza',
      terms: 'Términos y condiciones',
      privacy: 'Política de privacidad',
      contact: 'Contacto',
      rights: 'Todos los derechos reservados.',
      madeWith: 'Hecho con cariño en las Islas Canarias',
      taxNote: 'Todos los precios incluyen IGIC.',
    },
    activity: {
      overview: 'Descripción',
      included: 'Incluido',
      excluded: 'No incluido',
      itinerary: 'Itinerario',
      meetingPoint: 'Punto de encuentro',
      reviews: 'Reseñas',
      faq: 'Preguntas frecuentes',
      highlights: 'Destacados',
      duration: 'Duración',
      groupSize: 'Tamaño del grupo',
      smallGroupDefault: 'Grupo reducido',
      pickupDefault: 'Te confirmaremos el punto exacto y las condiciones de recogida tras la reserva.',
      languages: 'Idiomas',
      difficulty: 'Dificultad',
      minAge: 'Edad mínima',
      cancellationPolicy: 'Cancelación',
      whatToBring: 'Qué llevar',
      selectDate: 'Selecciona fecha',
      adults: 'Adultos',
      children: 'Niños',
      infants: 'Bebés',
      total: 'Total',
      checkAvailability: 'Ver disponibilidad',
      relatedTitle: 'También te puede gustar',
    },
    activitiesPage: {
      eyebrow: 'Actividades',
      headingPre: 'Todas las ',
      headingEm: 'aventuras',
      subtitle: 'Encuentra tu próxima experiencia entre más de 120 actividades curadas.',
      filters: 'Filtros',
      hide: 'Ocultar filtros',
      searchPlaceholder: 'Buscar…',
      destinations: 'Destinos',
      categories: 'Categorías',
      price: 'Precio',
      resultsFound: 'actividades encontradas',
      perPage: 'Mostrar:',
      sort: 'Ordenar:',
      sortFeatured: 'Destacados',
      sortPriceAsc: 'Precio: menor a mayor',
      sortPriceDesc: 'Precio: mayor a menor',
      sortRating: 'Mejor valorados',
      viewList: 'Ver en lista',
      viewGrid: 'Ver en cuadrícula',
      loadError: 'No pudimos cargar las actividades ahora mismo. Inténtalo de nuevo en unos minutos.',
      empty: 'No hay actividades con estos filtros.',
    },
    activityDetail: {
      back: 'Volver a actividades',
      notFound: 'Actividad no encontrada.',
      share: 'Compartir actividad',
      save: 'Guardar actividad',
      newActivity: 'Nueva actividad en OWA',
      verified: 'Operador local verificado',
      onRequest: 'Reserva bajo petición',
      prevImage: 'Imagen anterior',
      nextImage: 'Imagen siguiente',
      tabDesc: 'Descripción',
      tabIncluded: 'Qué incluye',
      tabMeeting: 'Punto de encuentro',
      tabReviews: 'Reseñas',
      about: 'Sobre la experiencia',
      group: 'Grupo',
      insurance: 'Seguro',
      video: 'Mira la experiencia',
      cancelTitle: 'Política de cancelación',
      requirements: 'Requisitos importantes',
      faqDuration: '¿Cuánto dura esta actividad?',
      faqIncluded: '¿Qué incluye esta actividad?',
      faqNotIncluded: '¿Qué no incluye?',
      faqMeetingPoint: '¿Dónde está el punto de encuentro?',
      faqCancellation: '¿Cuál es la política de cancelación?',
      faqRequirements: '¿Hay requisitos o restricciones?',
      notIncludedH: 'No incluido',
      whereMeet: 'Dónde nos encontramos',
      ratingOf5: 'sobre 5',
      reviewsRefNote: 'valoraciones de referencia. Publicaremos reseñas verificadas propias cuando haya reservas directas en OWA.',
      newReviewsNote: 'Publicaremos reseñas verificadas cuando se completen las primeras reservas.',
      dateLabel: 'Fecha',
      timeLabel: 'Hora',
            couponPlaceholder: "Código de descuento",
            addonsTitle: "Extras opcionales",
            addonPerPerson: "por persona",
            couponApply: "Aplicar",
            couponDiscount: "Descuento",
            couponInvalid: "Cupón no válido.",
            dateUnavailable: "Esta fecha no está disponible.",
            soldOut: "Agotado",
            seatsLeft: "{count} libres",
      fullName: 'Nombre completo',
      phone: 'Teléfono',
      each: 'cada uno',
      acceptPolicy: 'Acepto la política de esta actividad:',
      payingSumup: 'Pagar de forma segura con SumUp',
      openingSumup: 'Abriendo SumUp…',
      payPrep: 'Pago online en preparación',
      sendingRequest: 'Enviando solicitud…',
      requestBooking: 'Solicitar reserva',
      confirmWhatsApp: 'Confirmar disponibilidad por WhatsApp',
      whatsappDoubts: '¿Tienes dudas? Escríbenos por WhatsApp',
      payTotalEur: 'Pago total en EUR',
      checkoutNoteReady: 'El checkout ya está integrado. Falta publicar el backend seguro y las credenciales reales.',
      checkoutNoteRequest: 'Te contactaremos para confirmar disponibilidad y condiciones finales.',
      amountEurNote: 'El importe definitivo se procesa en EUR al precio oficial de la actividad.',
      payNowDeposit: 'Pagas ahora (depósito)',
      balanceOnBoard: 'Resto a pagar a bordo',
      tSelDateT: 'Selecciona una fecha',
      tSelDateD: 'Por favor elige el día para tu actividad.',
      tSelTimeT: 'Selecciona una hora',
      tSelTimeD: 'Elige uno de los horarios disponibles.',
      tSelPaxT: 'Selecciona participantes',
      tSelPaxD: 'Añade al menos una plaza antes de continuar.',
      tDataT: 'Completa tus datos',
      tDataD: 'Necesitamos nombre, email y teléfono para gestionar la reserva.',
      tAcceptT: 'Acepta la cancelación',
      tAcceptD: 'Debes aceptar la política específica de esta actividad.',
      tSentT: 'Solicitud enviada',
      tSentD: 'Te contactaremos muy pronto para confirmar disponibilidad y condiciones finales.',
      tSentFailT: 'No se pudo enviar la solicitud',
      tPayFailT: 'No se pudo iniciar el pago',
      tryAgain: 'Inténtalo de nuevo en unos minutos.',
    },
    places: {
      eyebrow: 'Destinos',
      headingPre: 'Islas ',
      headingEm: 'Canarias',
      subtitle: 'Ocho islas volcánicas, playas doradas, cielos limpios y actividades infinitas.',
      loadError: 'No pudimos cargar los destinos ahora mismo. Inténtalo de nuevo en unos minutos.',
      empty: 'Aún no hay destinos publicados.',
      canaries: 'Islas Canarias',
      activitiesAvailable: 'actividades disponibles',
      comingSoon: 'Próximamente',
    },
    notFound: {
      title: 'Página no encontrada',
      description: 'La página que buscas no existe o se ha movido.',
      home: 'Volver al inicio',
      activities: 'Ver actividades',

      islandNotFound: 'Isla no encontrada.',
      backDest: 'Todos los destinos',
      experiencesIn: 'Experiencias en',
      viewCatalog: 'Ver en el catálogo',
      preparingTitle: 'Aún estamos preparando',
      preparingText: 'Todavía no hay experiencias publicadas en esta isla. Cuéntanos qué buscas y te preparamos una propuesta personalizada.',
      whatsapp: 'Consultar por WhatsApp',
      contactUs: 'Contáctanos',
      catNotFound: 'Categoría no encontrada.',
      backActivities: 'Todas las actividades',
      experiences: 'experiencias',
      soonTitle: 'Pronto más experiencias',
      soonText: 'Estamos ampliando el catálogo de esta categoría. Mientras, explora todas las actividades.',
      viewCatalogBtn: 'Ver el catálogo',
    },
    about: {
      eyebrow: 'Sobre OWA',
      headingPre: 'Aventuras diseñadas ',
      headingEm: 'para exploradores',
      intro: 'OWA — Origin Wild Adventure nace en 2018 en Gran Canaria con un objetivo claro: ofrecer experiencias auténticas, sostenibles y memorables en las Islas Canarias. Nada de tours multitudinarios. Solo grupos pequeños, guías locales y las mejores localizaciones.',
      yearsBig: '7+ años',
      yearsSmall: 'creando aventuras',
      valuesEyebrow: 'Nuestros valores',
      valuesHeadingPre: 'Lo que nos ',
      valuesHeadingEm: 'mueve',
      v1Title: 'Autenticidad',
      v1Text: 'Rutas y experiencias reales, lejos de las masas.',
      v2Title: 'Pasión',
      v2Text: 'Cada aventura está creada con amor por el detalle.',
      v3Title: 'Sostenibilidad',
      v3Text: 'Cuidamos las islas para las próximas generaciones.',
      v4Title: 'Comunidad',
      v4Text: 'Trabajamos con proveedores 100% canarios locales.',
    },

    contact: {
      eyebrow: 'Contacto',
      headingPre: 'Estamos aquí ',
      headingEm: 'para ti',
      subtitle: 'Cuéntanos qué aventura tienes en mente y te ayudaremos a hacerla realidad.',
      office: 'Oficina central',
      writeUs: 'Escríbenos',
      formIntro: 'Rellena el formulario y nuestro equipo se pondrá en contacto contigo lo antes posible.',
      name: 'Nombre',
      subject: 'Asunto',
      messagePlaceholder: 'Cuéntanos qué aventura te interesa...',
      send: 'Enviar mensaje',
      sentTitle: '¡Mensaje enviado!',
      sentText: 'Te responderemos en menos de 24 horas.',
    },
    auth: {
      sessionExpired: "Tu sesión ha caducado. Vuelve a iniciar sesión.",
            orContinue: "O continúa con",
            continueFacebook: "Continuar con Facebook",
            loginEyebrow: 'Área cliente',
      loginTitle: 'Accede a tu cuenta',
      loginDesc: 'Entra para ver tus reservas y gestionar tu perfil.',
      password: 'Contraseña',
      forgot: 'Recuperar acceso',
      loggingIn: 'Accediendo…',
      login: 'Entrar a mi cuenta',
      noAccount: '¿Todavía no tienes cuenta?',
      createAccount: 'Crear cuenta',
      loginError: 'No se pudo iniciar sesión.',
      passwordPlaceholder: 'Tu contraseña',
      benefitsTitle: 'Tu cuenta OWA',
      benefit1: 'Consulta y gestiona tus reservas',
      benefit2: 'Guarda tus actividades favoritas',
      benefit3: 'Recibe ofertas exclusivas para exploradores',
      regEyebrow: 'Nuevo usuario',
      regTitle: 'Crea tu cuenta',
      regDesc: 'Regístrate para reservar más rápido y guardar tus aventuras favoritas.',
      fullName: 'Nombre completo',
      fullNamePlaceholder: 'Nombre y apellidos',
      phone: 'Teléfono',
      passwordMin: 'Mínimo 8 caracteres',
      repeatPassword: 'Repetir contraseña',
      repeatPasswordPlaceholder: 'Repite la contraseña',
      haveAccount: 'Ya tengo cuenta',
      creatingAccount: 'Creando cuenta…',
      passwordMismatch: 'Las contraseñas no coinciden.',
      registerError: 'No se pudo crear la cuenta.',
      accountEyebrow: 'Mi cuenta',
      noSessionTitle: 'No hay sesión activa',
      noSessionDesc: 'Inicia sesión para ver tus reservas y gestionar tu perfil.',
      loginBtn: 'Iniciar sesión',
      privateEyebrow: 'Área privada',
      hello: 'Hola,',
      myAccount: 'Mi cuenta',
      accountDesc: 'Resumen de tu perfil y tus reservas.',
      logout: 'Cerrar sesión',
      loggedOut: 'Sesión cerrada correctamente.',
      profile: 'Perfil',
      profileSub: 'Datos para la gestión de tus reservas.',
      role: 'Rol',
      company: 'Empresa',
      directCustomer: 'Cliente directo',
      notProvided: 'No indicado',
      myBookings: 'Mis reservas',
      bookingsSub: 'Tu historial y próximas salidas.',
      updating: 'Actualizando',
      bookingsError: 'No se pudieron cargar las reservas.',
      noBookingsTitle: 'Sin reservas todavía',
      noBookingsText: 'Cuando hagas tu primera reserva, aquí verás tus próximas actividades, importes y estados.',
      reference: 'Referencia',
      datePending: 'Fecha pendiente',
      amountPending: 'Importe pendiente',
      stConfirmed: 'Confirmada',
      stPaymentPending: 'Pago pendiente',
      stPaymentFailed: 'Pago fallido',
      stPending: 'Pendiente',
      stCancelled: 'Cancelada',
    },
    common: {
      loading: 'Cargando…',
      error: 'Algo ha fallado.',
      retry: 'Reintentar',
      close: 'Cerrar',
      back: 'Atrás',
      next: 'Siguiente',
      previous: 'Anterior',
      showMore: 'Ver más',
      showLess: 'Ver menos',
      needHelp: '¿Te ayudamos?',
    },
    whatsapp: {
      label: '¿Necesitas ayuda?',
      aria: 'Contáctanos por WhatsApp',
      prefill: 'Hola OWA, me gustaría información sobre una experiencia en las Islas Canarias.',
    },
  },

  // ══════════════════════════════════════════
  it: {
    cookies: { text: 'Usiamo i cookie per analizzare il traffico e migliorare la tua esperienza.', policy: 'Informativa sulla privacy', accept: 'Accetta', reject: 'Rifiuta' },
    nav: {
      home: 'Home',
      activities: 'Attività',
      destinations: 'Destinazioni',
      blog: 'Blog',
      about: 'Chi siamo',
      contact: 'Contatti',
      language: 'Lingua',
      currency: 'Valuta',
      dayMode: 'Modalità giorno',
      nightMode: 'Modalità notte',
    },
    hero: {
      eyebrow: 'ORIGIN WILD ADVENTURE',
      title: 'One Wild Adventure',
      subtitle: 'Non per tutti.',
      description: 'Pensata per chi cerca più di un tour tradizionale. Scopri le Isole Canarie via mare, aria e terra.',
      island: 'Isola',
      allIslands: 'Tutte le isole',
      searchDestination: 'Destinazione',
      searchPlaceholder: 'Dove andiamo?',
      searchFrom: 'Andata',
      searchTo: 'Ritorno',
      searchAddDate: 'Aggiungi data',
      searchButton: 'Cerca',
      searchActivity: 'Attività o luogo',
      searchActivityPlaceholder: 'Quale avventura cerchi?',
      searchDate: 'Data',
      searchAnyDate: 'Qualsiasi data',
      searchParticipants: 'Partecipanti',
      searchPeople: 'persone',
    },
    trust: {
      freeCancellation: 'Cancellazione gratuita 24h',
      deposit: 'Prenota con solo il 20%',
      localGuides: 'Guide locali certificate',
    },
    promise: {
      fairPricingTitle: 'Prezzo giusto e trasparente',
      fairPricingText: 'Il prezzo che vedi è quello che paghi, senza costi nascosti né sorprese.',
      clearCancellationTitle: 'Cancellazione chiara',
      clearCancellationText: "Ogni attività mostra la sua politica di cancellazione prima della prenotazione.",
      directSupportTitle: 'Assistenza diretta',
      directSupportText: 'Ti consigliamo e confermiamo la disponibilità all\'istante su WhatsApp.',
    },
    destinations: {
      eyebrow: 'Esplora',
      title: 'Le migliori destinazioni',
      titleAccent: 'delle Canarie',
      viewAll: 'Vedi tutte le destinazioni',
      activityCount: 'attività',
      activityCountOne: 'attività',
    },
    inspired: {
      eyebrow: 'Ispirazione',
      title: 'Lasciati ispirare per il prossimo viaggio',
      viewAll: 'Vedi tutte le attività',
    },
    card: {
      featured: 'In evidenza',
      from: 'Da',
      reviews: 'recensioni',
      reviewsOne: 'recensione',
      noReviews: 'Nuovo',
      bookNow: 'Prenota',
      viewDetails: 'Vedi dettagli',
      perPerson: 'a persona',
    },
    why: {
      eyebrow: "Dove inizia l'avventura",
      title: 'Non per tutti.',
      titleAccent: 'Solo per esploratori.',
      description: 'OWA nasce dalla passione per la natura selvaggia. Non vendiamo tour: creiamo avventure su misura via mare, aria e terra nelle isole più spettacolari dell\u2019Atlantico.',
      statCustomers: 'Ospiti soddisfatti',
      statActivities: 'Attività',
      statIslands: 'Isole coperte',
      statRating: 'Valutazione media',
      feature1Title: 'Guide locali',
      feature1Text: 'Il nostro team è nato alle Canarie e conosce ogni angolo delle isole.',
      feature2Title: 'Avventure premium',
      feature2Text: 'Esperienze curate nei minimi dettagli, non tour generici da agenzia.',
      feature3Title: 'Gruppi piccoli',
      feature3Text: 'Massimo 8 persone per attività. Più vicini, più autentici.',
      feature4Title: 'Prenotazione sicura',
      feature4Text: 'Cancellazione gratuita fino a 24 ore prima. Pagamento 100% sicuro.',
    },
    bestOf: {
      eyebrow: 'Il meglio delle Canarie',
      title: 'Best of Canary',
      description: 'Attività selezionate con cura dalle nostre guide locali. Filtra per categoria e trova la tua prossima avventura.',
      filterAll: 'Tutte',
      filterWater: 'Attività Nautiche',
      filterDiving: 'Immersioni & Snorkeling',
      filterLand: 'Avventura & Terra',
      filterAir: 'Sport Aerei',
      filterNature: 'Natura & Fauna',
      filterFood: 'Gastronomia',
    },
    categoryChips: {
      barco: 'Barca',
      delfines: 'Delfini',
      jetSki: 'Jet ski',
      buggy: 'Buggy',
      buceo: 'Immersioni',
      familias: 'Famiglie',
    },
    testimonials: {
      eyebrow: 'Testimonianze',
      title: 'Storie vere di esploratori',
      empty: 'Sii il primo a condividere la tua esperienza.',
    },
    blog: {
      eyebrow: 'Blog',
      title: 'Storie, consigli e guide',
      viewAll: 'Vedi tutti gli articoli',
      readMore: 'Leggi di più',
      minRead: 'min di lettura',
    },
    socialFeed: {
      eyebrow: 'Seguici',
      title: 'In diretta dalle isole',
      subtitle: 'Momenti reali delle nostre avventure su Instagram e TikTok.',
    },
    blogPage: {
      heading: 'Storie, consigli e guide',
      subtitle: 'Ispirazione, consigli e guide per scoprire le Isole Canarie come un local.',
      topics: 'Temi',
      all: 'Tutti',
    },
    blogDetail: {
      notFound: 'Articolo non trovato',
      notFoundText: 'Il post che cerchi non esiste o è stato rimosso.',
      back: 'Torna al blog',
      author: 'Team OWA',
      keepReading: 'Continua a leggere',
      noContent: 'Contenuto non disponibile.',
      ctaTitle: 'Pronto a incontrare i giganti dell’Atlantico?',
      ctaText: 'Sali a bordo dell’Ocean Giants Cruise e vivi l’avvistamento di globicefali, capodogli e delfini con guida biologo marino, idrofono e bagno in una cala segreta.',
      ctaBook: 'Prenota l’Ocean Giants Cruise',
      ctaAsk: 'Chiedici quello che vuoi',
    },
    newsletter: {
      title: 'Offerte esclusive per wild explorers',
      description: 'Iscriviti e ricevi sconti riservati e le migliori avventure ogni settimana.',
      placeholder: 'La tua email',
      button: 'Iscriviti',
      disclaimer: 'Niente spam. Puoi disiscriverti quando vuoi.',
      success: 'Grazie per l\u2019iscrizione.',
      error: 'Qualcosa è andato storto. Riprova.',
    },
    wishlist: {
      eyebrow: 'La tua selezione',
      title: 'Preferiti',
      subtitle: 'Salva le avventure che ami e ritrovale qui.',
      countSuffix: 'avventure salvate',
      clearAll: 'Svuota tutto',
      emptyTitle: 'Non hai ancora preferiti',
      emptyBody: 'Tocca il cuore su qualsiasi avventura per salvarla qui.',
      exploreCta: 'Esplora le avventure',
      add: 'Aggiungi ai preferiti',
      remove: 'Rimuovi dai preferiti',
    },
    pagination: {
      label: 'Paginazione',
      perPage: 'Mostra:',
      prev: 'Pagina precedente',
      next: 'Pagina successiva',
    },
    footer: {
      description: 'Origin Wild Adventure. Pensata per chi cerca più di un tour convenzionale alle Isole Canarie.',
      company: 'Azienda',
      about: 'Chi siamo',
      blog: 'Blog',
      careers: 'Lavora con noi',
      press: 'Stampa',
      affiliates: 'Affiliati',
      help: 'Aiuto',
      helpCenter: 'Centro assistenza',
      cancellation: 'Politica di cancellazione',
      safety: 'Sicurezza e fiducia',
      terms: 'Termini e condizioni',
      privacy: 'Informativa sulla privacy',
      contact: 'Contatti',
      rights: 'Tutti i diritti riservati.',
      madeWith: 'Fatto con cura alle Isole Canarie',
      taxNote: 'Tutti i prezzi includono l\u2019IGIC.',
    },
    activity: {
      overview: 'Descrizione',
      included: 'Incluso',
      excluded: 'Non incluso',
      itinerary: 'Itinerario',
      meetingPoint: 'Punto d\u2019incontro',
      reviews: 'Recensioni',
      faq: 'Domande frequenti',
      highlights: 'Punti salienti',
      duration: 'Durata',
      groupSize: 'Dimensione del gruppo',
      smallGroupDefault: 'Gruppo ridotto',
      pickupDefault: 'Ti confermeremo il punto esatto e le condizioni di ritiro dopo la prenotazione.',
      languages: 'Lingue',
      difficulty: 'Difficoltà',
      minAge: 'Età minima',
      cancellationPolicy: 'Cancellazione',
      whatToBring: 'Cosa portare',
      selectDate: 'Seleziona la data',
      adults: 'Adulti',
      children: 'Bambini',
      infants: 'Neonati',
      total: 'Totale',
      checkAvailability: 'Verifica disponibilità',
      relatedTitle: 'Potrebbe interessarti',
    },
    activitiesPage: {
      eyebrow: 'Attività',
      headingPre: 'Tutte le ',
      headingEm: 'avventure',
      subtitle: 'Trova la tua prossima esperienza tra più di 120 attività selezionate.',
      filters: 'Filtri',
      hide: 'Nascondi filtri',
      searchPlaceholder: 'Cerca…',
      destinations: 'Destinazioni',
      categories: 'Categorie',
      price: 'Prezzo',
      resultsFound: 'attività trovate',
      perPage: 'Mostra:',
      sort: 'Ordina:',
      sortFeatured: 'In evidenza',
      sortPriceAsc: 'Prezzo: dal più basso',
      sortPriceDesc: 'Prezzo: dal più alto',
      sortRating: 'Più votate',
      viewList: 'Vista elenco',
      viewGrid: 'Vista griglia',
      loadError: 'Non siamo riusciti a caricare le attività in questo momento. Riprova tra qualche minuto.',
      empty: 'Nessuna attività con questi filtri.',
    },
    activityDetail: {
      back: 'Torna alle attività',
      notFound: 'Attività non trovata.',
      share: 'Condividi attività',
      save: 'Salva attività',
      newActivity: 'Nuova attività su OWA',
      verified: 'Operatore locale verificato',
      onRequest: 'Prenotazione su richiesta',
      prevImage: 'Immagine precedente',
      nextImage: 'Immagine successiva',
      tabDesc: 'Descrizione',
      tabIncluded: 'Cosa include',
      tabMeeting: 'Punto d’incontro',
      tabReviews: 'Recensioni',
      about: 'Sull’esperienza',
      group: 'Gruppo',
      insurance: 'Assicurazione',
      video: "Guarda l'esperienza",
      cancelTitle: 'Politica di cancellazione',
      requirements: 'Requisiti importanti',
      faqDuration: 'Quanto dura questa attività?',
      faqIncluded: 'Cosa include questa attività?',
      faqNotIncluded: 'Cosa non è incluso?',
      faqMeetingPoint: "Dov'è il punto d'incontro?",
      faqCancellation: 'Qual è la politica di cancellazione?',
      faqRequirements: 'Ci sono requisiti o restrizioni?',
      notIncludedH: 'Non incluso',
      whereMeet: 'Dove ci incontriamo',
      ratingOf5: 'su 5',
      reviewsRefNote: 'valutazioni di riferimento. Pubblicheremo recensioni verificate quando ci saranno prenotazioni dirette su OWA.',
      newReviewsNote: 'Pubblicheremo recensioni verificate una volta completate le prime prenotazioni.',
      dateLabel: 'Data',
      timeLabel: 'Ora',
            couponPlaceholder: "Codice sconto",
            addonsTitle: "Extra opzionali",
            addonPerPerson: "a persona",
            couponApply: "Applica",
            couponDiscount: "Sconto",
            couponInvalid: "Coupon non valido.",
            dateUnavailable: "Questa data non è disponibile.",
            soldOut: "Esaurito",
            seatsLeft: "{count} liberi",
      fullName: 'Nome completo',
      phone: 'Telefono',
      each: 'ciascuno',
      acceptPolicy: 'Accetto la politica di questa attività:',
      payingSumup: 'Paga in sicurezza con SumUp',
      openingSumup: 'Apertura di SumUp…',
      payPrep: 'Pagamento online in arrivo',
      sendingRequest: 'Invio richiesta…',
      requestBooking: 'Richiedi prenotazione',
      confirmWhatsApp: 'Conferma disponibilità su WhatsApp',
      whatsappDoubts: 'Hai dubbi? Scrivici su WhatsApp',
      payTotalEur: 'Pagamento totale in EUR',
      checkoutNoteReady: 'Il checkout è già integrato. Restano da pubblicare il backend sicuro e le credenziali reali.',
      checkoutNoteRequest: 'Ti contatteremo per confermare disponibilità e condizioni finali.',
      amountEurNote: 'L’importo definitivo viene elaborato in EUR al prezzo ufficiale dell’attività.',
      payNowDeposit: 'Paghi ora (deposito)',
      balanceOnBoard: 'Saldo da pagare a bordo',
      tSelDateT: 'Seleziona una data',
      tSelDateD: 'Scegli il giorno per la tua attività.',
      tSelTimeT: 'Seleziona un orario',
      tSelTimeD: 'Scegli uno degli orari disponibili.',
      tSelPaxT: 'Seleziona i partecipanti',
      tSelPaxD: 'Aggiungi almeno un posto prima di continuare.',
      tDataT: 'Completa i tuoi dati',
      tDataD: 'Ci servono nome, email e telefono per gestire la prenotazione.',
      tAcceptT: 'Accetta la cancellazione',
      tAcceptD: 'Devi accettare la politica specifica di questa attività.',
      tSentT: 'Richiesta inviata',
      tSentD: 'Ti contatteremo molto presto per confermare disponibilità e condizioni finali.',
      tSentFailT: 'Impossibile inviare la richiesta',
      tPayFailT: 'Impossibile avviare il pagamento',
      tryAgain: 'Riprova tra qualche minuto.',
    },
    places: {
      eyebrow: 'Destinazioni',
      headingPre: 'Isole ',
      headingEm: 'Canarie',
      subtitle: 'Otto isole vulcaniche, spiagge dorate, cieli limpidi e attività infinite.',
      loadError: 'Non siamo riusciti a caricare le destinazioni in questo momento. Riprova tra qualche minuto.',
      empty: 'Nessuna destinazione pubblicata al momento.',
      canaries: 'Isole Canarie',
      activitiesAvailable: 'attività disponibili',
      comingSoon: 'Prossimamente',
    },
    notFound: {
      title: 'Pagina non trovata',
      description: 'La pagina che cerchi non esiste o è stata spostata.',
      home: 'Torna alla home',
      activities: 'Vedi attività',

      islandNotFound: 'Isola non trovata.',
      backDest: 'Tutte le destinazioni',
      experiencesIn: 'Esperienze a',
      viewCatalog: 'Vedi nel catalogo',
      preparingTitle: 'Stiamo ancora preparando',
      preparingText: 'Non ci sono ancora esperienze pubblicate su questa isola. Dicci cosa cerchi e prepareremo una proposta personalizzata.',
      whatsapp: 'Chiedi su WhatsApp',
      contactUs: 'Contattaci',
      catNotFound: 'Categoria non trovata.',
      backActivities: 'Tutte le attività',
      experiences: 'esperienze',
      soonTitle: 'Presto altre esperienze',
      soonText: 'Stiamo ampliando questa categoria. Nel frattempo, esplora tutte le attività.',
      viewCatalogBtn: 'Vedi il catalogo',
    },
    about: {
      eyebrow: 'Chi è OWA',
      headingPre: 'Avventure pensate ',
      headingEm: 'per esploratori',
      intro: 'OWA — Origin Wild Adventure nasce nel 2018 a Gran Canaria con un obiettivo chiaro: offrire esperienze autentiche, sostenibili e memorabili alle Isole Canarie. Niente tour affollati. Solo piccoli gruppi, guide locali e le migliori location.',
      yearsBig: '7+ anni',
      yearsSmall: 'a creare avventure',
      valuesEyebrow: 'I nostri valori',
      valuesHeadingPre: 'Ciò che ci ',
      valuesHeadingEm: 'muove',
      v1Title: 'Autenticità',
      v1Text: 'Percorsi ed esperienze reali, lontano dalla folla.',
      v2Title: 'Passione',
      v2Text: 'Ogni avventura è creata con amore per il dettaglio.',
      v3Title: 'Sostenibilità',
      v3Text: 'Ci prendiamo cura delle isole per le generazioni future.',
      v4Title: 'Comunità',
      v4Text: 'Lavoriamo con fornitori 100% locali delle Canarie.',
    },

    contact: {
      eyebrow: 'Contatti',
      headingPre: 'Siamo qui ',
      headingEm: 'per te',
      subtitle: 'Raccontaci quale avventura hai in mente e ti aiuteremo a realizzarla.',
      office: 'Sede centrale',
      writeUs: 'Scrivici',
      formIntro: 'Compila il modulo e il nostro team ti contatterà il prima possibile.',
      name: 'Nome',
      subject: 'Oggetto',
      messagePlaceholder: 'Raccontaci quale avventura ti interessa…',
      send: 'Invia messaggio',
      sentTitle: 'Messaggio inviato!',
      sentText: 'Ti risponderemo entro 24 ore.',
    },
    auth: {
      sessionExpired: "La tua sessione è scaduta. Accedi di nuovo.",
            orContinue: "Oppure continua con",
            continueFacebook: "Continua con Facebook",
            loginEyebrow: 'Area cliente',
      loginTitle: 'Accedi al tuo account',
      loginDesc: 'Accedi per vedere le tue prenotazioni e gestire il tuo profilo.',
      password: 'Password',
      forgot: 'Recupera accesso',
      loggingIn: 'Accesso in corso…',
      login: 'Accedi',
      noAccount: 'Non hai ancora un account?',
      createAccount: 'Crea account',
      loginError: 'Impossibile accedere.',
      passwordPlaceholder: 'La tua password',
      benefitsTitle: 'Il tuo account OWA',
      benefit1: 'Consulta e gestisci le tue prenotazioni',
      benefit2: 'Salva le tue attività preferite',
      benefit3: 'Ricevi offerte esclusive per esploratori',
      regEyebrow: 'Nuovo utente',
      regTitle: 'Crea il tuo account',
      regDesc: 'Registrati per prenotare più velocemente e salvare le tue avventure preferite.',
      fullName: 'Nome completo',
      fullNamePlaceholder: 'Nome e cognome',
      phone: 'Telefono',
      passwordMin: 'Almeno 8 caratteri',
      repeatPassword: 'Ripeti password',
      repeatPasswordPlaceholder: 'Ripeti la password',
      haveAccount: 'Ho già un account',
      creatingAccount: 'Creazione account…',
      passwordMismatch: 'Le password non coincidono.',
      registerError: 'Impossibile creare l’account.',
      accountEyebrow: 'Il mio account',
      noSessionTitle: 'Nessuna sessione attiva',
      noSessionDesc: 'Accedi per vedere le tue prenotazioni e gestire il tuo profilo.',
      loginBtn: 'Accedi',
      privateEyebrow: 'Area privata',
      hello: 'Ciao,',
      myAccount: 'Il mio account',
      accountDesc: 'Un riepilogo del tuo profilo e delle tue prenotazioni.',
      logout: 'Esci',
      loggedOut: 'Disconnessione avvenuta correttamente.',
      profile: 'Profilo',
      profileSub: 'Dati per la gestione delle tue prenotazioni.',
      role: 'Ruolo',
      company: 'Azienda',
      directCustomer: 'Cliente diretto',
      notProvided: 'Non indicato',
      myBookings: 'Le mie prenotazioni',
      bookingsSub: 'La tua cronologia e le prossime uscite.',
      updating: 'Aggiornamento',
      bookingsError: 'Impossibile caricare le prenotazioni.',
      noBookingsTitle: 'Ancora nessuna prenotazione',
      noBookingsText: 'Dopo la tua prima prenotazione, qui vedrai le prossime attività, gli importi e gli stati.',
      reference: 'Riferimento',
      datePending: 'Data da definire',
      amountPending: 'Importo da definire',
      stConfirmed: 'Confermata',
      stPaymentPending: 'Pagamento in sospeso',
      stPaymentFailed: 'Pagamento fallito',
      stPending: 'In sospeso',
      stCancelled: 'Annullata',
    },
    common: {
      loading: 'Caricamento…',
      error: 'Qualcosa è andato storto.',
      retry: 'Riprova',
      close: 'Chiudi',
      back: 'Indietro',
      next: 'Avanti',
      previous: 'Precedente',
      showMore: 'Mostra di più',
      showLess: 'Mostra meno',
      needHelp: 'Ti aiutiamo?',
    },
    whatsapp: {
      label: 'Serve aiuto?',
      aria: 'Contattaci su WhatsApp',
      prefill: "Ciao OWA, vorrei informazioni su un'esperienza nelle Isole Canarie.",
    },
  },

  // ══════════════════════════════════════════
  de: {
    cookies: { text: 'Wir verwenden Cookies, um den Traffic zu analysieren und dein Erlebnis zu verbessern.', policy: 'Datenschutzerklärung', accept: 'Akzeptieren', reject: 'Ablehnen' },
    nav: {
      home: 'Start',
      activities: 'Aktivitäten',
      destinations: 'Reiseziele',
      blog: 'Blog',
      about: 'Über uns',
      contact: 'Kontakt',
      language: 'Sprache',
      currency: 'Währung',
      dayMode: 'Tagmodus',
      nightMode: 'Nachtmodus',
    },
    hero: {
      eyebrow: 'ORIGIN WILD ADVENTURE',
      title: 'One Wild Adventure',
      subtitle: 'Nicht für jeden.',
      description: 'Für alle, die mehr suchen als eine typische Tour. Entdecke die Kanarischen Inseln zu Wasser, in der Luft und an Land.',
      island: 'Insel',
      allIslands: 'Alle Inseln',
      searchDestination: 'Reiseziel',
      searchPlaceholder: 'Wohin geht es?',
      searchFrom: 'Hinreise',
      searchTo: 'Rückreise',
      searchAddDate: 'Datum wählen',
      searchButton: 'Suchen',
      searchActivity: 'Aktivität oder Ort',
      searchActivityPlaceholder: 'Welches Abenteuer suchst du?',
      searchDate: 'Datum',
      searchAnyDate: 'Beliebiges Datum',
      searchParticipants: 'Teilnehmer',
      searchPeople: 'Personen',
    },
    trust: {
      freeCancellation: 'Kostenlose Stornierung 24 Std.',
      deposit: 'Buchen mit nur 20%',
      localGuides: 'Zertifizierte lokale Guides',
    },
    promise: {
      fairPricingTitle: 'Fairer, transparenter Preis',
      fairPricingText: 'Der Preis, den du siehst, ist der Preis, den du zahlst — keine versteckten Kosten, keine Überraschungen.',
      clearCancellationTitle: 'Klare Stornierung',
      clearCancellationText: 'Jede Aktivität zeigt ihre Stornierungsbedingungen, bevor du buchst.',
      directSupportTitle: 'Direkter Support',
      directSupportText: 'Wir beraten dich und bestätigen die Verfügbarkeit sofort über WhatsApp.',
    },
    destinations: {
      eyebrow: 'Entdecken',
      title: 'Die besten Reiseziele',
      titleAccent: 'der Kanaren',
      viewAll: 'Alle Reiseziele ansehen',
      activityCount: 'Aktivitäten',
      activityCountOne: 'Aktivität',
    },
    inspired: {
      eyebrow: 'Inspiration',
      title: 'Lass dich für deine nächste Reise inspirieren',
      viewAll: 'Alle Aktivitäten ansehen',
    },
    card: {
      featured: 'Empfohlen',
      from: 'Ab',
      reviews: 'Bewertungen',
      reviewsOne: 'Bewertung',
      noReviews: 'Neu',
      bookNow: 'Jetzt buchen',
      viewDetails: 'Details ansehen',
      perPerson: 'pro Person',
    },
    why: {
      eyebrow: 'Wo das Abenteuer beginnt',
      title: 'Nicht für jeden.',
      titleAccent: 'Nur für Entdecker.',
      description: 'OWA entstand aus der Leidenschaft für die Wildnis. Wir verkaufen keine Touren: Wir gestalten maßgeschneiderte Abenteuer zu Wasser, in der Luft und an Land auf den spektakulärsten Inseln des Atlantiks.',
      statCustomers: 'Zufriedene Gäste',
      statActivities: 'Aktivitäten',
      statIslands: 'Abgedeckte Inseln',
      statRating: 'Durchschnittsbewertung',
      feature1Title: 'Lokale Guides',
      feature1Text: 'Unser Team ist auf den Kanaren geboren und kennt jeden Winkel der Inseln.',
      feature2Title: 'Premium-Abenteuer',
      feature2Text: 'Bis ins Detail kuratierte Erlebnisse, keine generischen Agenturtouren.',
      feature3Title: 'Kleine Gruppen',
      feature3Text: 'Maximal 8 Personen pro Aktivität. Näher dran, authentischer.',
      feature4Title: 'Sichere Buchung',
      feature4Text: 'Kostenlose Stornierung bis 24 Stunden vorher. 100% sichere Zahlung.',
    },
    bestOf: {
      eyebrow: 'Das Beste der Kanaren',
      title: 'Best of Canary',
      description: 'Von unseren lokalen Guides sorgfältig ausgewählte Aktivitäten. Filtere nach Kategorie und finde dein nächstes Abenteuer.',
      filterAll: 'Alle',
      filterWater: 'Wassersport',
      filterDiving: 'Tauchen & Schnorcheln',
      filterLand: 'Abenteuer & Land',
      filterAir: 'Luftsport',
      filterNature: 'Natur & Tierwelt',
      filterFood: 'Kulinarik',
    },
    categoryChips: {
      barco: 'Boot',
      delfines: 'Delfine',
      jetSki: 'Jetski',
      buggy: 'Buggy',
      buceo: 'Tauchen',
      familias: 'Familien',
    },
    testimonials: {
      eyebrow: 'Erfahrungsberichte',
      title: 'Echte Geschichten von Entdeckern',
      empty: 'Teile als Erster deine Erfahrung.',
    },
    blog: {
      eyebrow: 'Blog',
      title: 'Geschichten, Tipps & Guides',
      viewAll: 'Alle Artikel ansehen',
      readMore: 'Weiterlesen',
      minRead: 'Min. Lesezeit',
    },
    socialFeed: {
      eyebrow: 'Folge uns',
      title: 'Live von den Inseln',
      subtitle: 'Echte Momente unserer Abenteuer auf Instagram und TikTok.',
    },
    blogPage: {
      heading: 'Geschichten, Tipps & Guides',
      subtitle: 'Inspiration, Tipps und Guides, um die Kanarischen Inseln wie ein Local zu entdecken.',
      topics: 'Themen',
      all: 'Alle',
    },
    blogDetail: {
      notFound: 'Artikel nicht gefunden',
      notFoundText: 'Der gesuchte Beitrag existiert nicht oder wurde entfernt.',
      back: 'Zurück zum Blog',
      author: 'OWA-Team',
      keepReading: 'Weiterlesen',
      noContent: 'Inhalt nicht verfügbar.',
      ctaTitle: 'Bereit, die Giganten des Atlantiks zu treffen?',
      ctaText: 'Komm an Bord des Ocean Giants Cruise und erlebe Grindwale, Pottwale und Delfine mit einem Meeresbiologen als Guide, Hydrofon und einem Bad in einer geheimen Bucht.',
      ctaBook: 'Ocean Giants Cruise buchen',
      ctaAsk: 'Frag uns alles',
    },
    newsletter: {
      title: 'Exklusive Angebote für wild explorers',
      description: 'Abonniere und erhalte geheime Rabatte und die besten Abenteuer jede Woche.',
      placeholder: 'Deine E-Mail-Adresse',
      button: 'Abonnieren',
      disclaimer: 'Kein Spam. Jederzeit abbestellbar.',
      success: 'Danke für dein Abonnement.',
      error: 'Etwas ist schiefgelaufen. Bitte erneut versuchen.',
    },
    wishlist: {
      eyebrow: 'Deine Auswahl',
      title: 'Favoriten',
      subtitle: 'Speichere deine Lieblingsabenteuer und finde sie hier wieder.',
      countSuffix: 'gespeicherte Abenteuer',
      clearAll: 'Alle entfernen',
      emptyTitle: 'Du hast noch keine Favoriten',
      emptyBody: 'Tippe bei einem Abenteuer auf das Herz, um es hier zu speichern.',
      exploreCta: 'Abenteuer entdecken',
      add: 'Zu Favoriten hinzufügen',
      remove: 'Aus Favoriten entfernen',
    },
    pagination: {
      label: 'Seitennummerierung',
      perPage: 'Anzeigen:',
      prev: 'Vorherige Seite',
      next: 'Nächste Seite',
    },
    footer: {
      description: 'Origin Wild Adventure. Für alle, die mehr suchen als eine konventionelle Tour auf den Kanarischen Inseln.',
      company: 'Unternehmen',
      about: 'Über uns',
      blog: 'Blog',
      careers: 'Arbeite mit uns',
      press: 'Presse',
      affiliates: 'Partner',
      help: 'Hilfe',
      helpCenter: 'Hilfezentrum',
      cancellation: 'Stornierungsbedingungen',
      safety: 'Sicherheit & Vertrauen',
      terms: 'AGB',
      privacy: 'Datenschutzerklärung',
      contact: 'Kontakt',
      rights: 'Alle Rechte vorbehalten.',
      madeWith: 'Mit Sorgfalt auf den Kanarischen Inseln gemacht',
      taxNote: 'Alle Preise inklusive IGIC.',
    },
    activity: {
      overview: 'Beschreibung',
      included: 'Inbegriffen',
      excluded: 'Nicht inbegriffen',
      itinerary: 'Ablauf',
      meetingPoint: 'Treffpunkt',
      reviews: 'Bewertungen',
      faq: 'Häufige Fragen',
      highlights: 'Highlights',
      duration: 'Dauer',
      groupSize: 'Gruppengröße',
      smallGroupDefault: 'Kleine Gruppe',
      pickupDefault: 'Wir bestätigen dir den genauen Abholpunkt und die Bedingungen nach der Buchung.',
      languages: 'Sprachen',
      difficulty: 'Schwierigkeit',
      minAge: 'Mindestalter',
      cancellationPolicy: 'Stornierung',
      whatToBring: 'Was mitbringen',
      selectDate: 'Datum wählen',
      adults: 'Erwachsene',
      children: 'Kinder',
      infants: 'Kleinkinder',
      total: 'Gesamt',
      checkAvailability: 'Verfügbarkeit prüfen',
      relatedTitle: 'Könnte dir auch gefallen',
    },
    activitiesPage: {
      eyebrow: 'Aktivitäten',
      headingPre: 'Alle ',
      headingEm: 'Abenteuer',
      subtitle: 'Finde dein nächstes Erlebnis unter mehr als 120 kuratierten Aktivitäten.',
      filters: 'Filter',
      hide: 'Filter ausblenden',
      searchPlaceholder: 'Suchen…',
      destinations: 'Reiseziele',
      categories: 'Kategorien',
      price: 'Preis',
      resultsFound: 'Aktivitäten gefunden',
      perPage: 'Anzeigen:',
      sort: 'Sortieren:',
      sortFeatured: 'Empfohlen',
      sortPriceAsc: 'Preis: aufsteigend',
      sortPriceDesc: 'Preis: absteigend',
      sortRating: 'Bestbewertet',
      viewList: 'Listenansicht',
      viewGrid: 'Rasteransicht',
      loadError: 'Wir konnten die Aktivitäten gerade nicht laden. Bitte versuche es in ein paar Minuten erneut.',
      empty: 'Keine Aktivitäten mit diesen Filtern.',
    },
    activityDetail: {
      back: 'Zurück zu den Aktivitäten',
      notFound: 'Aktivität nicht gefunden.',
      share: 'Aktivität teilen',
      save: 'Aktivität speichern',
      newActivity: 'Neue Aktivität bei OWA',
      verified: 'Verifizierter lokaler Anbieter',
      onRequest: 'Buchung auf Anfrage',
      prevImage: 'Vorheriges Bild',
      nextImage: 'Nächstes Bild',
      tabDesc: 'Beschreibung',
      tabIncluded: 'Was ist inklusive',
      tabMeeting: 'Treffpunkt',
      tabReviews: 'Bewertungen',
      about: 'Über das Erlebnis',
      group: 'Gruppe',
      insurance: 'Versicherung',
      video: 'Erlebnis ansehen',
      cancelTitle: 'Stornierungsbedingungen',
      requirements: 'Wichtige Voraussetzungen',
      faqDuration: 'Wie lange dauert diese Aktivität?',
      faqIncluded: 'Was ist bei dieser Aktivität inbegriffen?',
      faqNotIncluded: 'Was ist nicht enthalten?',
      faqMeetingPoint: 'Wo ist der Treffpunkt?',
      faqCancellation: 'Wie lautet die Stornierungsbedingung?',
      faqRequirements: 'Gibt es Voraussetzungen oder Einschränkungen?',
      notIncludedH: 'Nicht inbegriffen',
      whereMeet: 'Wo wir uns treffen',
      ratingOf5: 'von 5',
      reviewsRefNote: 'Referenzbewertungen. Wir veröffentlichen eigene verifizierte Bewertungen, sobald es Direktbuchungen bei OWA gibt.',
      newReviewsNote: 'Wir veröffentlichen verifizierte Bewertungen, sobald die ersten Buchungen abgeschlossen sind.',
      dateLabel: 'Datum',
      timeLabel: 'Uhrzeit',
            couponPlaceholder: "Rabattcode",
            addonsTitle: "Optionale Extras",
            addonPerPerson: "pro Person",
            couponApply: "Anwenden",
            couponDiscount: "Rabatt",
            couponInvalid: "Ungültiger Gutschein.",
            dateUnavailable: "Dieses Datum ist nicht verfügbar.",
            soldOut: "Ausverkauft",
            seatsLeft: "{count} frei",
      fullName: 'Vollständiger Name',
      phone: 'Telefon',
      each: 'pro Stück',
      acceptPolicy: 'Ich akzeptiere die Bedingungen dieser Aktivität:',
      payingSumup: 'Sicher mit SumUp bezahlen',
      openingSumup: 'SumUp wird geöffnet…',
      payPrep: 'Online-Zahlung bald verfügbar',
      sendingRequest: 'Anfrage wird gesendet…',
      requestBooking: 'Buchung anfragen',
      confirmWhatsApp: 'Verfügbarkeit per WhatsApp bestätigen',
      whatsappDoubts: 'Fragen? Schreib uns auf WhatsApp',
      payTotalEur: 'Vollständige Zahlung in EUR',
      checkoutNoteReady: 'Der Checkout ist bereits integriert. Das sichere Backend und die echten Zugangsdaten müssen noch live gehen.',
      checkoutNoteRequest: 'Wir kontaktieren dich, um Verfügbarkeit und endgültige Bedingungen zu bestätigen.',
      amountEurNote: 'Der endgültige Betrag wird in EUR zum offiziellen Preis der Aktivität verarbeitet.',
      payNowDeposit: 'Jetzt zahlen (Anzahlung)',
      balanceOnBoard: 'Restbetrag an Bord',
      tSelDateT: 'Datum auswählen',
      tSelDateD: 'Bitte wähle den Tag für deine Aktivität.',
      tSelTimeT: 'Uhrzeit auswählen',
      tSelTimeD: 'Wähle eine der verfügbaren Uhrzeiten.',
      tSelPaxT: 'Teilnehmer auswählen',
      tSelPaxD: 'Füge mindestens einen Platz hinzu, bevor du fortfährst.',
      tDataT: 'Vervollständige deine Daten',
      tDataD: 'Wir benötigen Name, E-Mail und Telefon, um die Buchung zu verwalten.',
      tAcceptT: 'Stornierung akzeptieren',
      tAcceptD: 'Du musst die spezifischen Bedingungen dieser Aktivität akzeptieren.',
      tSentT: 'Anfrage gesendet',
      tSentD: 'Wir kontaktieren dich sehr bald, um Verfügbarkeit und endgültige Bedingungen zu bestätigen.',
      tSentFailT: 'Anfrage konnte nicht gesendet werden',
      tPayFailT: 'Zahlung konnte nicht gestartet werden',
      tryAgain: 'Bitte versuche es in ein paar Minuten erneut.',
    },
    places: {
      eyebrow: 'Reiseziele',
      headingPre: 'Kanarische ',
      headingEm: 'Inseln',
      subtitle: 'Acht Vulkaninseln, goldene Strände, klarer Himmel und unendliche Aktivitäten.',
      loadError: 'Wir konnten die Reiseziele gerade nicht laden. Bitte versuche es in ein paar Minuten erneut.',
      empty: 'Noch keine Reiseziele veröffentlicht.',
      canaries: 'Kanarische Inseln',
      activitiesAvailable: 'Aktivitäten verfügbar',
      comingSoon: 'Demnächst',
    },
    notFound: {
      title: 'Seite nicht gefunden',
      description: 'Die gesuchte Seite existiert nicht oder wurde verschoben.',
      home: 'Zurück zur Startseite',
      activities: 'Aktivitäten ansehen',

      islandNotFound: 'Insel nicht gefunden.',
      backDest: 'Alle Reiseziele',
      experiencesIn: 'Erlebnisse auf',
      viewCatalog: 'Im Katalog ansehen',
      preparingTitle: 'Wir arbeiten noch an',
      preparingText: 'Auf dieser Insel sind noch keine Erlebnisse veröffentlicht. Sag uns, was du suchst, und wir stellen dir ein persönliches Angebot zusammen.',
      whatsapp: 'Auf WhatsApp fragen',
      contactUs: 'Kontaktiere uns',
      catNotFound: 'Kategorie nicht gefunden.',
      backActivities: 'Alle Aktivitäten',
      experiences: 'Erlebnisse',
      soonTitle: 'Bald mehr Erlebnisse',
      soonText: 'Wir erweitern diese Kategorie. Entdecke in der Zwischenzeit alle Aktivitäten.',
      viewCatalogBtn: 'Katalog ansehen',
    },
    about: {
      eyebrow: 'Über OWA',
      headingPre: 'Abenteuer gestaltet ',
      headingEm: 'für Entdecker',
      intro: 'OWA — Origin Wild Adventure entstand 2018 auf Gran Canaria mit einem klaren Ziel: authentische, nachhaltige und unvergessliche Erlebnisse auf den Kanarischen Inseln zu bieten. Keine Massentouren. Nur kleine Gruppen, lokale Guides und die besten Orte.',
      yearsBig: '7+ Jahre',
      yearsSmall: 'gestalten wir Abenteuer',
      valuesEyebrow: 'Unsere Werte',
      valuesHeadingPre: 'Was uns ',
      valuesHeadingEm: 'antreibt',
      v1Title: 'Authentizität',
      v1Text: 'Echte Routen und Erlebnisse, abseits der Massen.',
      v2Title: 'Leidenschaft',
      v2Text: 'Jedes Abenteuer wird mit Liebe zum Detail gestaltet.',
      v3Title: 'Nachhaltigkeit',
      v3Text: 'Wir schützen die Inseln für die nächsten Generationen.',
      v4Title: 'Gemeinschaft',
      v4Text: 'Wir arbeiten mit 100% lokalen kanarischen Anbietern.',
    },

    contact: {
      eyebrow: 'Kontakt',
      headingPre: 'Wir sind ',
      headingEm: 'für dich da',
      subtitle: 'Sag uns, welches Abenteuer dir vorschwebt, und wir helfen dir, es wahr werden zu lassen.',
      office: 'Hauptbüro',
      writeUs: 'Schreib uns',
      formIntro: 'Fülle das Formular aus und unser Team setzt sich so schnell wie möglich mit dir in Verbindung.',
      name: 'Name',
      subject: 'Betreff',
      messagePlaceholder: 'Sag uns, welches Abenteuer dich interessiert…',
      send: 'Nachricht senden',
      sentTitle: 'Nachricht gesendet!',
      sentText: 'Wir antworten innerhalb von 24 Stunden.',
    },
    auth: {
      sessionExpired: "Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.",
            orContinue: "Oder weiter mit",
            continueFacebook: "Mit Facebook fortfahren",
            loginEyebrow: 'Kundenbereich',
      loginTitle: 'Melde dich in deinem Konto an',
      loginDesc: 'Melde dich an, um deine Buchungen zu sehen und dein Profil zu verwalten.',
      password: 'Passwort',
      forgot: 'Zugang wiederherstellen',
      loggingIn: 'Anmeldung läuft…',
      login: 'Anmelden',
      noAccount: 'Noch kein Konto?',
      createAccount: 'Konto erstellen',
      loginError: 'Anmeldung nicht möglich.',
      passwordPlaceholder: 'Dein Passwort',
      benefitsTitle: 'Dein OWA-Konto',
      benefit1: 'Buchungen einsehen und verwalten',
      benefit2: 'Lieblingsaktivitäten speichern',
      benefit3: 'Exklusive Angebote für Entdecker erhalten',
      regEyebrow: 'Neuer Nutzer',
      regTitle: 'Erstelle dein Konto',
      regDesc: 'Registriere dich, um schneller zu buchen und deine Lieblingsabenteuer zu speichern.',
      fullName: 'Vollständiger Name',
      fullNamePlaceholder: 'Vor- und Nachname',
      phone: 'Telefon',
      passwordMin: 'Mindestens 8 Zeichen',
      repeatPassword: 'Passwort wiederholen',
      repeatPasswordPlaceholder: 'Passwort wiederholen',
      haveAccount: 'Ich habe bereits ein Konto',
      creatingAccount: 'Konto wird erstellt…',
      passwordMismatch: 'Die Passwörter stimmen nicht überein.',
      registerError: 'Konto konnte nicht erstellt werden.',
      accountEyebrow: 'Mein Konto',
      noSessionTitle: 'Keine aktive Sitzung',
      noSessionDesc: 'Melde dich an, um deine Buchungen zu sehen und dein Profil zu verwalten.',
      loginBtn: 'Anmelden',
      privateEyebrow: 'Privater Bereich',
      hello: 'Hallo,',
      myAccount: 'Mein Konto',
      accountDesc: 'Eine Übersicht über dein Profil und deine Buchungen.',
      logout: 'Abmelden',
      loggedOut: 'Erfolgreich abgemeldet.',
      profile: 'Profil',
      profileSub: 'Daten für die Verwaltung deiner Buchungen.',
      role: 'Rolle',
      company: 'Unternehmen',
      directCustomer: 'Direktkunde',
      notProvided: 'Nicht angegeben',
      myBookings: 'Meine Buchungen',
      bookingsSub: 'Dein Verlauf und kommende Touren.',
      updating: 'Wird aktualisiert',
      bookingsError: 'Buchungen konnten nicht geladen werden.',
      noBookingsTitle: 'Noch keine Buchungen',
      noBookingsText: 'Sobald du deine erste Buchung machst, siehst du hier deine kommenden Aktivitäten, Beträge und Status.',
      reference: 'Referenz',
      datePending: 'Datum ausstehend',
      amountPending: 'Betrag ausstehend',
      stConfirmed: 'Bestätigt',
      stPaymentPending: 'Zahlung ausstehend',
      stPaymentFailed: 'Zahlung fehlgeschlagen',
      stPending: 'Ausstehend',
      stCancelled: 'Storniert',
    },
    common: {
      loading: 'Lädt…',
      error: 'Etwas ist schiefgelaufen.',
      retry: 'Erneut versuchen',
      close: 'Schließen',
      back: 'Zurück',
      next: 'Weiter',
      previous: 'Vorherige',
      showMore: 'Mehr anzeigen',
      showLess: 'Weniger anzeigen',
      needHelp: 'Brauchst du Hilfe?',
    },
    whatsapp: {
      label: 'Brauchst du Hilfe?',
      aria: 'Kontaktiere uns auf WhatsApp',
      prefill: 'Hallo OWA, ich hätte gerne Informationen zu einem Erlebnis auf den Kanarischen Inseln.',
    },
  },

  // ══════════════════════════════════════════
  no: {
    cookies: { text: 'Vi bruker informasjonskapsler for å analysere trafikk og forbedre opplevelsen din.', policy: 'Personvernerklæring', accept: 'Godta', reject: 'Avslå' },
    nav: {
      home: 'Hjem',
      activities: 'Aktiviteter',
      destinations: 'Destinasjoner',
      blog: 'Blogg',
      about: 'Om oss',
      contact: 'Kontakt',
      language: 'Språk',
      currency: 'Valuta',
      dayMode: 'Dagmodus',
      nightMode: 'Nattmodus',
    },
    hero: {
      eyebrow: 'ORIGIN WILD ADVENTURE',
      title: 'One Wild Adventure',
      subtitle: 'Ikke for alle.',
      description: 'Laget for deg som vil ha mer enn en vanlig tur. Oppdag Kanariøyene til sjøs, i luften og på land.',
      island: 'Øy',
      allIslands: 'Alle øyer',
      searchDestination: 'Destinasjon',
      searchPlaceholder: 'Hvor skal vi?',
      searchFrom: 'Fra',
      searchTo: 'Til',
      searchAddDate: 'Velg dato',
      searchButton: 'Søk',
      searchActivity: 'Aktivitet eller sted',
      searchActivityPlaceholder: 'Hvilket eventyr er du ute etter?',
      searchDate: 'Dato',
      searchAnyDate: 'Når som helst',
      searchParticipants: 'Deltakere',
      searchPeople: 'personer',
    },
    trust: {
      freeCancellation: 'Gratis avbestilling 24t',
      deposit: 'Book med kun 20%',
      localGuides: 'Sertifiserte lokale guider',
    },
    promise: {
      fairPricingTitle: 'Rettferdig, gjennomsiktig pris',
      fairPricingText: 'Prisen du ser er prisen du betaler — ingen skjulte avgifter, ingen overraskelser.',
      clearCancellationTitle: 'Tydelig avbestilling',
      clearCancellationText: 'Hver aktivitet viser sin avbestillingspolicy før du booker.',
      directSupportTitle: 'Direkte support',
      directSupportText: 'Vi gir deg råd og bekrefter tilgjengelighet umiddelbart på WhatsApp.',
    },
    destinations: {
      eyebrow: 'Utforsk',
      title: 'De beste destinasjonene',
      titleAccent: 'på Kanariøyene',
      viewAll: 'Se alle destinasjoner',
      activityCount: 'aktiviteter',
      activityCountOne: 'aktivitet',
    },
    inspired: {
      eyebrow: 'Inspirasjon',
      title: 'La deg inspirere til neste tur',
      viewAll: 'Se alle aktiviteter',
    },
    card: {
      featured: 'Anbefalt',
      from: 'Fra',
      reviews: 'anmeldelser',
      reviewsOne: 'anmeldelse',
      noReviews: 'Ny',
      bookNow: 'Book nå',
      viewDetails: 'Se detaljer',
      perPerson: 'per person',
    },
    why: {
      eyebrow: 'Der eventyret begynner',
      title: 'Ikke for alle.',
      titleAccent: 'Kun for utforskere.',
      description: 'OWA er født av lidenskap for det ville. Vi selger ikke turer: vi skaper skreddersydde eventyr til sjøs, i luften og på land på Atlanterhavets mest spektakulære øyer.',
      statCustomers: 'Fornøyde gjester',
      statActivities: 'Aktiviteter',
      statIslands: 'Øyer dekket',
      statRating: 'Gjennomsnittlig vurdering',
      feature1Title: 'Lokale guider',
      feature1Text: 'Teamet vårt er født på Kanariøyene og kjenner hver krok av øyene.',
      feature2Title: 'Premium-eventyr',
      feature2Text: 'Opplevelser kuratert ned til minste detalj, ikke generiske byråturer.',
      feature3Title: 'Små grupper',
      feature3Text: 'Maks 8 personer per aktivitet. Nærmere, mer autentisk.',
      feature4Title: 'Trygg booking',
      feature4Text: 'Gratis avbestilling inntil 24 timer før. 100% sikker betaling.',
    },
    bestOf: {
      eyebrow: 'Det beste av Kanariøyene',
      title: 'Best of Canary',
      description: 'Aktiviteter nøye utvalgt av våre lokale guider. Filtrer etter kategori og finn ditt neste eventyr.',
      filterAll: 'Alle',
      filterWater: 'Vannaktiviteter',
      filterDiving: 'Dykking & Snorkling',
      filterLand: 'Eventyr & Land',
      filterAir: 'Luftsport',
      filterNature: 'Natur & Dyreliv',
      filterFood: 'Mat & Drikke',
    },
    categoryChips: {
      barco: 'Båt',
      delfines: 'Delfiner',
      jetSki: 'Jetski',
      buggy: 'Buggy',
      buceo: 'Dykking',
      familias: 'Familier',
    },
    testimonials: {
      eyebrow: 'Anmeldelser',
      title: 'Ekte historier fra utforskere',
      empty: 'Bli den første til å dele din opplevelse.',
    },
    blog: {
      eyebrow: 'Blogg',
      title: 'Historier, tips og guider',
      viewAll: 'Se alle artikler',
      readMore: 'Les mer',
      minRead: 'min lesing',
    },
    socialFeed: {
      eyebrow: 'Følg oss',
      title: 'Direkte fra øyene',
      subtitle: 'Ekte øyeblikk fra våre eventyr på Instagram og TikTok.',
    },
    blogPage: {
      heading: 'Historier, tips og guider',
      subtitle: 'Inspirasjon, tips og guider for å oppdage Kanariøyene som en lokal.',
      topics: 'Temaer',
      all: 'Alle',
    },
    blogDetail: {
      notFound: 'Artikkelen ble ikke funnet',
      notFoundText: 'Innlegget du leter etter finnes ikke eller er fjernet.',
      back: 'Tilbake til bloggen',
      author: 'OWA-teamet',
      keepReading: 'Fortsett å lese',
      noContent: 'Innhold ikke tilgjengelig.',
      ctaTitle: 'Klar til å møte Atlanterhavets giganter?',
      ctaText: 'Kom om bord i Ocean Giants Cruise og opplev grindhval, spermhval og delfiner med marinbiolog som guide, hydrofon og bad i en hemmelig vik.',
      ctaBook: 'Book Ocean Giants Cruise',
      ctaAsk: 'Spør oss om hva som helst',
    },
    newsletter: {
      title: 'Eksklusive tilbud for wild explorers',
      description: 'Abonner og få hemmelige rabatter og de beste eventyrene hver uke.',
      placeholder: 'Din e-postadresse',
      button: 'Abonner',
      disclaimer: 'Ingen spam. Meld deg av når som helst.',
      success: 'Takk for at du abonnerer.',
      error: 'Noe gikk galt. Prøv igjen.',
    },
    wishlist: {
      eyebrow: 'Ditt utvalg',
      title: 'Favoritter',
      subtitle: 'Lagre eventyrene du liker og finn dem igjen her.',
      countSuffix: 'lagrede eventyr',
      clearAll: 'Tøm alle',
      emptyTitle: 'Du har ingen favoritter ennå',
      emptyBody: 'Trykk på hjertet på et eventyr for å lagre det her.',
      exploreCta: 'Utforsk eventyr',
      add: 'Legg til i favoritter',
      remove: 'Fjern fra favoritter',
    },
    pagination: {
      label: 'Paginering',
      perPage: 'Vis:',
      prev: 'Forrige side',
      next: 'Neste side',
    },
    footer: {
      description: 'Origin Wild Adventure. Laget for deg som vil ha mer enn en vanlig tur på Kanariøyene.',
      company: 'Selskap',
      about: 'Om oss',
      blog: 'Blogg',
      careers: 'Jobb hos oss',
      press: 'Presse',
      affiliates: 'Partnere',
      help: 'Hjelp',
      helpCenter: 'Hjelpesenter',
      cancellation: 'Avbestillingsvilkår',
      safety: 'Sikkerhet og tillit',
      terms: 'Vilkår og betingelser',
      privacy: 'Personvernerklæring',
      contact: 'Kontakt',
      rights: 'Alle rettigheter reservert.',
      madeWith: 'Laget med omtanke på Kanariøyene',
      taxNote: 'Alle priser inkluderer IGIC.',
    },
    activity: {
      overview: 'Beskrivelse',
      included: 'Inkludert',
      excluded: 'Ikke inkludert',
      itinerary: 'Program',
      meetingPoint: 'Møtested',
      reviews: 'Anmeldelser',
      faq: 'Ofte stilte spørsmål',
      highlights: 'Høydepunkter',
      duration: 'Varighet',
      groupSize: 'Gruppestørrelse',
      smallGroupDefault: 'Liten gruppe',
      pickupDefault: 'Vi bekrefter nøyaktig hentepunkt og vilkår etter bestilling.',
      languages: 'Språk',
      difficulty: 'Vanskelighetsgrad',
      minAge: 'Minstealder',
      cancellationPolicy: 'Avbestilling',
      whatToBring: 'Hva du bør ta med',
      selectDate: 'Velg dato',
      adults: 'Voksne',
      children: 'Barn',
      infants: 'Spedbarn',
      total: 'Totalt',
      checkAvailability: 'Sjekk tilgjengelighet',
      relatedTitle: 'Du vil kanskje også like',
    },
    activitiesPage: {
      eyebrow: 'Aktiviteter',
      headingPre: 'Alle ',
      headingEm: 'eventyr',
      subtitle: 'Finn din neste opplevelse blant mer enn 120 utvalgte aktiviteter.',
      filters: 'Filtre',
      hide: 'Skjul filtre',
      searchPlaceholder: 'Søk…',
      destinations: 'Destinasjoner',
      categories: 'Kategorier',
      price: 'Pris',
      resultsFound: 'aktiviteter funnet',
      perPage: 'Vis:',
      sort: 'Sorter:',
      sortFeatured: 'Anbefalt',
      sortPriceAsc: 'Pris: lav til høy',
      sortPriceDesc: 'Pris: høy til lav',
      sortRating: 'Best vurdert',
      viewList: 'Listevisning',
      viewGrid: 'Rutenettvisning',
      loadError: 'Vi kunne ikke laste aktivitetene akkurat nå. Prøv igjen om noen minutter.',
      empty: 'Ingen aktiviteter med disse filtrene.',
    },
    activityDetail: {
      back: 'Tilbake til aktiviteter',
      notFound: 'Aktiviteten ble ikke funnet.',
      share: 'Del aktivitet',
      save: 'Lagre aktivitet',
      newActivity: 'Ny aktivitet på OWA',
      verified: 'Verifisert lokal operatør',
      onRequest: 'Booking på forespørsel',
      prevImage: 'Forrige bilde',
      nextImage: 'Neste bilde',
      tabDesc: 'Beskrivelse',
      tabIncluded: 'Hva som er inkludert',
      tabMeeting: 'Møtested',
      tabReviews: 'Anmeldelser',
      about: 'Om opplevelsen',
      group: 'Gruppe',
      insurance: 'Forsikring',
      video: 'Se opplevelsen',
      cancelTitle: 'Avbestillingsvilkår',
      requirements: 'Viktige krav',
      faqDuration: 'Hvor lenge varer denne aktiviteten?',
      faqIncluded: 'Hva er inkludert i denne aktiviteten?',
      faqNotIncluded: 'Hva er ikke inkludert?',
      faqMeetingPoint: 'Hvor er møtepunktet?',
      faqCancellation: 'Hva er avbestillingspolitikken?',
      faqRequirements: 'Er det noen krav eller restriksjoner?',
      notIncludedH: 'Ikke inkludert',
      whereMeet: 'Hvor vi møtes',
      ratingOf5: 'av 5',
      reviewsRefNote: 'referansevurderinger. Vi publiserer egne verifiserte anmeldelser når det finnes direktebookinger på OWA.',
      newReviewsNote: 'Vi publiserer verifiserte anmeldelser når de første bookingene er fullført.',
      dateLabel: 'Dato',
      timeLabel: 'Tid',
            couponPlaceholder: "Rabattkode",
            addonsTitle: "Valgfrie tillegg",
            addonPerPerson: "per person",
            couponApply: "Bruk",
            couponDiscount: "Rabatt",
            couponInvalid: "Ugyldig kupong.",
            dateUnavailable: "Denne datoen er ikke tilgjengelig.",
            soldOut: "Utsolgt",
            seatsLeft: "{count} ledige",
      fullName: 'Fullt navn',
      phone: 'Telefon',
      each: 'per stk',
      acceptPolicy: 'Jeg godtar vilkårene for denne aktiviteten:',
      payingSumup: 'Betal trygt med SumUp',
      openingSumup: 'Åpner SumUp…',
      payPrep: 'Nettbetaling kommer snart',
      sendingRequest: 'Sender forespørsel…',
      requestBooking: 'Be om booking',
      confirmWhatsApp: 'Bekreft tilgjengelighet på WhatsApp',
      whatsappDoubts: 'Spørsmål? Skriv til oss på WhatsApp',
      payTotalEur: 'Full betaling i EUR',
      checkoutNoteReady: 'Checkout er allerede integrert. Sikker backend og ekte legitimasjon må fortsatt settes i drift.',
      checkoutNoteRequest: 'Vi kontakter deg for å bekrefte tilgjengelighet og endelige betingelser.',
      amountEurNote: 'Det endelige beløpet behandles i EUR til aktivitetens offisielle pris.',
      payNowDeposit: 'Betal nå (depositum)',
      balanceOnBoard: 'Restbeløp betales om bord',
      tSelDateT: 'Velg en dato',
      tSelDateD: 'Velg dagen for aktiviteten din.',
      tSelTimeT: 'Velg et tidspunkt',
      tSelTimeD: 'Velg et av de tilgjengelige tidspunktene.',
      tSelPaxT: 'Velg deltakere',
      tSelPaxD: 'Legg til minst én plass før du fortsetter.',
      tDataT: 'Fullfør opplysningene dine',
      tDataD: 'Vi trenger navn, e-post og telefon for å håndtere bookingen.',
      tAcceptT: 'Godta avbestillingen',
      tAcceptD: 'Du må godta de spesifikke vilkårene for denne aktiviteten.',
      tSentT: 'Forespørsel sendt',
      tSentD: 'Vi kontakter deg svært snart for å bekrefte tilgjengelighet og endelige betingelser.',
      tSentFailT: 'Kunne ikke sende forespørselen',
      tPayFailT: 'Kunne ikke starte betalingen',
      tryAgain: 'Prøv igjen om noen minutter.',
    },
    places: {
      eyebrow: 'Destinasjoner',
      headingPre: 'Kanari',
      headingEm: 'øyene',
      subtitle: 'Åtte vulkanske øyer, gyllne strender, klar himmel og uendelige aktiviteter.',
      loadError: 'Vi kunne ikke laste destinasjonene akkurat nå. Prøv igjen om noen minutter.',
      empty: 'Ingen destinasjoner publisert ennå.',
      canaries: 'Kanariøyene',
      activitiesAvailable: 'aktiviteter tilgjengelig',
      comingSoon: 'Kommer snart',
    },
    notFound: {
      title: 'Siden ble ikke funnet',
      description: 'Siden du leter etter finnes ikke eller er flyttet.',
      home: 'Tilbake til forsiden',
      activities: 'Se aktiviteter',

      islandNotFound: 'Øya ble ikke funnet.',
      backDest: 'Alle destinasjoner',
      experiencesIn: 'Opplevelser på',
      viewCatalog: 'Se i katalogen',
      preparingTitle: 'Vi forbereder fortsatt',
      preparingText: 'Det er ennå ingen opplevelser publisert på denne øya. Fortell oss hva du er ute etter, så setter vi sammen et personlig forslag.',
      whatsapp: 'Spør på WhatsApp',
      contactUs: 'Kontakt oss',
      catNotFound: 'Kategorien ble ikke funnet.',
      backActivities: 'Alle aktiviteter',
      experiences: 'opplevelser',
      soonTitle: 'Flere opplevelser snart',
      soonText: 'Vi utvider denne kategorien. I mellomtiden kan du utforske alle aktiviteter.',
      viewCatalogBtn: 'Se katalogen',
    },
    about: {
      eyebrow: 'Om OWA',
      headingPre: 'Eventyr laget ',
      headingEm: 'for utforskere',
      intro: 'OWA — Origin Wild Adventure ble født i 2018 på Gran Canaria med et klart mål: å tilby autentiske, bærekraftige og minneverdige opplevelser på Kanariøyene. Ingen overfylte turer. Bare små grupper, lokale guider og de beste stedene.',
      yearsBig: '7+ år',
      yearsSmall: 'med å skape eventyr',
      valuesEyebrow: 'Våre verdier',
      valuesHeadingPre: 'Det som ',
      valuesHeadingEm: 'driver oss',
      v1Title: 'Autentisitet',
      v1Text: 'Ekte ruter og opplevelser, borte fra folkemengdene.',
      v2Title: 'Lidenskap',
      v2Text: 'Hvert eventyr er skapt med kjærlighet til detaljer.',
      v3Title: 'Bærekraft',
      v3Text: 'Vi tar vare på øyene for kommende generasjoner.',
      v4Title: 'Fellesskap',
      v4Text: 'Vi samarbeider med 100% lokale leverandører fra Kanariøyene.',
    },

    contact: {
      eyebrow: 'Kontakt',
      headingPre: 'Vi er her ',
      headingEm: 'for deg',
      subtitle: 'Fortell oss hvilket eventyr du har i tankene, så hjelper vi deg med å gjøre det til virkelighet.',
      office: 'Hovedkontor',
      writeUs: 'Skriv til oss',
      formIntro: 'Fyll ut skjemaet, så tar teamet vårt kontakt med deg så snart som mulig.',
      name: 'Navn',
      subject: 'Emne',
      messagePlaceholder: 'Fortell oss hvilket eventyr du er interessert i…',
      send: 'Send melding',
      sentTitle: 'Melding sendt!',
      sentText: 'Vi svarer innen 24 timer.',
    },
    auth: {
      sessionExpired: "Økten din har utløpt. Logg inn på nytt.",
            orContinue: "Eller fortsett med",
            continueFacebook: "Fortsett med Facebook",
            loginEyebrow: 'Kundeområde',
      loginTitle: 'Logg inn på kontoen din',
      loginDesc: 'Logg inn for å se bookingene dine og administrere profilen din.',
      password: 'Passord',
      forgot: 'Gjenopprett tilgang',
      loggingIn: 'Logger inn…',
      login: 'Logg inn',
      noAccount: 'Har du ikke konto ennå?',
      createAccount: 'Opprett konto',
      loginError: 'Kunne ikke logge inn.',
      passwordPlaceholder: 'Passordet ditt',
      benefitsTitle: 'OWA-kontoen din',
      benefit1: 'Se og administrer bookingene dine',
      benefit2: 'Lagre favorittaktivitetene dine',
      benefit3: 'Få eksklusive tilbud for utforskere',
      regEyebrow: 'Ny bruker',
      regTitle: 'Opprett kontoen din',
      regDesc: 'Registrer deg for å booke raskere og lagre favorittopplevelsene dine.',
      fullName: 'Fullt navn',
      fullNamePlaceholder: 'Fornavn og etternavn',
      phone: 'Telefon',
      passwordMin: 'Minst 8 tegn',
      repeatPassword: 'Gjenta passord',
      repeatPasswordPlaceholder: 'Gjenta passordet',
      haveAccount: 'Jeg har allerede konto',
      creatingAccount: 'Oppretter konto…',
      passwordMismatch: 'Passordene stemmer ikke overens.',
      registerError: 'Kunne ikke opprette kontoen.',
      accountEyebrow: 'Min konto',
      noSessionTitle: 'Ingen aktiv økt',
      noSessionDesc: 'Logg inn for å se bookingene dine og administrere profilen din.',
      loginBtn: 'Logg inn',
      privateEyebrow: 'Privat område',
      hello: 'Hei,',
      myAccount: 'Min konto',
      accountDesc: 'En oversikt over profilen og bookingene dine.',
      logout: 'Logg ut',
      loggedOut: 'Logget ut.',
      profile: 'Profil',
      profileSub: 'Opplysninger for å administrere bookingene dine.',
      role: 'Rolle',
      company: 'Selskap',
      directCustomer: 'Direktekunde',
      notProvided: 'Ikke oppgitt',
      myBookings: 'Mine bookinger',
      bookingsSub: 'Historikken din og kommende turer.',
      updating: 'Oppdaterer',
      bookingsError: 'Kunne ikke laste bookingene.',
      noBookingsTitle: 'Ingen bookinger ennå',
      noBookingsText: 'Når du gjør din første booking, ser du kommende aktiviteter, beløp og statuser her.',
      reference: 'Referanse',
      datePending: 'Dato kommer',
      amountPending: 'Beløp kommer',
      stConfirmed: 'Bekreftet',
      stPaymentPending: 'Betaling venter',
      stPaymentFailed: 'Betaling mislyktes',
      stPending: 'Venter',
      stCancelled: 'Kansellert',
    },
    common: {
      loading: 'Laster…',
      error: 'Noe gikk galt.',
      retry: 'Prøv igjen',
      close: 'Lukk',
      back: 'Tilbake',
      next: 'Neste',
      previous: 'Forrige',
      showMore: 'Vis mer',
      showLess: 'Vis mindre',
      needHelp: 'Trenger du hjelp?',
    },
    whatsapp: {
      label: 'Trenger du hjelp?',
      aria: 'Kontakt oss på WhatsApp',
      prefill: 'Hei OWA, jeg vil gjerne ha informasjon om en opplevelse på Kanariøyene.',
    },
  },

  // ══════════════════════════════════════════
  nl: {
    cookies: { text: 'We gebruiken cookies om het verkeer te analyseren en je ervaring te verbeteren.', policy: 'Privacybeleid', accept: 'Accepteren', reject: 'Weigeren' },
    nav: {
      home: 'Home',
      activities: 'Activiteiten',
      destinations: 'Bestemmingen',
      blog: 'Blog',
      about: 'Over ons',
      contact: 'Contact',
      language: 'Taal',
      currency: 'Valuta',
      dayMode: 'Dagmodus',
      nightMode: 'Nachtmodus',
    },
    hero: {
      eyebrow: 'ORIGIN WILD ADVENTURE',
      title: 'One Wild Adventure',
      subtitle: 'Niet voor iedereen.',
      description: 'Ontworpen voor wie meer zoekt dan een gewone tour. Ontdek de Canarische Eilanden over zee, door de lucht en over land.',
      island: 'Eiland',
      allIslands: 'Alle eilanden',
      searchDestination: 'Bestemming',
      searchPlaceholder: 'Waar gaan we heen?',
      searchFrom: 'Heen',
      searchTo: 'Terug',
      searchAddDate: 'Datum toevoegen',
      searchButton: 'Zoeken',
      searchActivity: 'Activiteit of plaats',
      searchActivityPlaceholder: 'Welk avontuur zoek je?',
      searchDate: 'Datum',
      searchAnyDate: 'Elke datum',
      searchParticipants: 'Deelnemers',
      searchPeople: 'personen',
    },
    trust: {
      freeCancellation: 'Gratis annuleren 24u',
      deposit: 'Boek met slechts 20%',
      localGuides: 'Gecertificeerde lokale gidsen',
    },
    promise: {
      fairPricingTitle: 'Eerlijke, transparante prijzen',
      fairPricingText: 'De prijs die je ziet is de prijs die je betaalt — geen verborgen kosten, geen verrassingen.',
      clearCancellationTitle: 'Duidelijke annulering',
      clearCancellationText: 'Elke activiteit toont het annuleringsbeleid voordat je boekt.',
      directSupportTitle: 'Directe ondersteuning',
      directSupportText: 'We adviseren je en bevestigen de beschikbaarheid direct via WhatsApp.',
    },
    destinations: {
      eyebrow: 'Ontdek',
      title: 'De beste bestemmingen',
      titleAccent: 'van de Canarische Eilanden',
      viewAll: 'Bekijk alle bestemmingen',
      activityCount: 'activiteiten',
      activityCountOne: 'activiteit',
    },
    inspired: {
      eyebrow: 'Inspiratie',
      title: 'Laat je inspireren voor je volgende reis',
      viewAll: 'Bekijk alle activiteiten',
    },
    card: {
      featured: 'Uitgelicht',
      from: 'Vanaf',
      reviews: 'beoordelingen',
      reviewsOne: 'beoordeling',
      noReviews: 'Nieuw',
      bookNow: 'Boek nu',
      viewDetails: 'Bekijk details',
      perPerson: 'per persoon',
    },
    why: {
      eyebrow: 'Waar het avontuur begint',
      title: 'Niet voor iedereen.',
      titleAccent: 'Alleen voor ontdekkers.',
      description: 'OWA is ontstaan uit passie voor het wilde. Wij verkopen geen tours: wij creëren avonturen op maat over zee, door de lucht en over land op de meest spectaculaire eilanden van de Atlantische Oceaan.',
      statCustomers: 'Tevreden gasten',
      statActivities: 'Activiteiten',
      statIslands: 'Eilanden gedekt',
      statRating: 'Gemiddelde beoordeling',
      feature1Title: 'Lokale gidsen',
      feature1Text: 'Ons team is geboren op de Canarische Eilanden en kent elke hoek van de eilanden.',
      feature2Title: 'Premium avonturen',
      feature2Text: 'Tot in detail samengestelde ervaringen, geen generieke bureautours.',
      feature3Title: 'Kleine groepen',
      feature3Text: 'Maximaal 8 personen per activiteit. Dichterbij, authentieker.',
      feature4Title: 'Veilig boeken',
      feature4Text: 'Gratis annuleren tot 24 uur van tevoren. 100% veilige betaling.',
    },
    bestOf: {
      eyebrow: 'Het beste van de Canarische Eilanden',
      title: 'Best of Canary',
      description: 'Activiteiten zorgvuldig geselecteerd door onze lokale gidsen. Filter op categorie en vind je volgende avontuur.',
      filterAll: 'Alle',
      filterWater: 'Wateractiviteiten',
      filterDiving: 'Duiken & Snorkelen',
      filterLand: 'Avontuur & Land',
      filterAir: 'Luchtsporten',
      filterNature: 'Natuur & Dieren',
      filterFood: 'Eten & Drinken',
    },
    categoryChips: {
      barco: 'Boot',
      delfines: 'Dolfijnen',
      jetSki: 'Jetski',
      buggy: 'Buggy',
      buceo: 'Duiken',
      familias: 'Families',
    },
    testimonials: {
      eyebrow: 'Ervaringen',
      title: 'Echte verhalen van ontdekkers',
      empty: 'Wees de eerste die zijn ervaring deelt.',
    },
    blog: {
      eyebrow: 'Blog',
      title: 'Verhalen, tips & gidsen',
      viewAll: 'Bekijk alle artikelen',
      readMore: 'Lees meer',
      minRead: 'min leestijd',
    },
    socialFeed: {
      eyebrow: 'Volg ons',
      title: 'Live vanaf de eilanden',
      subtitle: 'Echte momenten van onze avonturen op Instagram en TikTok.',
    },
    blogPage: {
      heading: 'Verhalen, tips & gidsen',
      subtitle: 'Inspiratie, tips en gidsen om de Canarische Eilanden als een local te ontdekken.',
      topics: 'Onderwerpen',
      all: 'Alle',
    },
    blogDetail: {
      notFound: 'Artikel niet gevonden',
      notFoundText: 'Het bericht dat je zoekt bestaat niet of is verwijderd.',
      back: 'Terug naar blog',
      author: 'OWA-team',
      keepReading: 'Verder lezen',
      noContent: 'Inhoud niet beschikbaar.',
      ctaTitle: 'Klaar om de reuzen van de Atlantische Oceaan te ontmoeten?',
      ctaText: 'Kom aan boord van de Ocean Giants Cruise en beleef griendwalvissen, potvissen en dolfijnen met een marien bioloog als gids, een hydrofoon en een duik in een geheime baai.',
      ctaBook: 'Boek de Ocean Giants Cruise',
      ctaAsk: 'Vraag ons alles',
    },
    newsletter: {
      title: 'Exclusieve aanbiedingen voor wild explorers',
      description: 'Abonneer je en ontvang geheime kortingen en de beste avonturen elke week.',
      placeholder: 'Je e-mailadres',
      button: 'Abonneren',
      disclaimer: 'Geen spam. Op elk moment opzegbaar.',
      success: 'Bedankt voor je aanmelding.',
      error: 'Er ging iets mis. Probeer het opnieuw.',
    },
    wishlist: {
      eyebrow: 'Jouw selectie',
      title: 'Favorieten',
      subtitle: 'Bewaar de avonturen die je leuk vindt en vind ze hier terug.',
      countSuffix: 'bewaarde avonturen',
      clearAll: 'Alles wissen',
      emptyTitle: 'Je hebt nog geen favorieten',
      emptyBody: 'Tik op het hartje bij een avontuur om het hier te bewaren.',
      exploreCta: 'Avonturen ontdekken',
      add: 'Toevoegen aan favorieten',
      remove: 'Verwijderen uit favorieten',
    },
    pagination: {
      label: 'Paginering',
      perPage: 'Toon:',
      prev: 'Vorige pagina',
      next: 'Volgende pagina',
    },
    footer: {
      description: 'Origin Wild Adventure. Ontworpen voor wie meer zoekt dan een conventionele tour op de Canarische Eilanden.',
      company: 'Bedrijf',
      about: 'Over ons',
      blog: 'Blog',
      careers: 'Werken bij ons',
      press: 'Pers',
      affiliates: 'Partners',
      help: 'Hulp',
      helpCenter: 'Helpcentrum',
      cancellation: 'Annuleringsbeleid',
      safety: 'Veiligheid & vertrouwen',
      terms: 'Algemene voorwaarden',
      privacy: 'Privacybeleid',
      contact: 'Contact',
      rights: 'Alle rechten voorbehouden.',
      madeWith: 'Met zorg gemaakt op de Canarische Eilanden',
      taxNote: 'Alle prijzen zijn inclusief IGIC.',
    },
    activity: {
      overview: 'Beschrijving',
      included: 'Inbegrepen',
      excluded: 'Niet inbegrepen',
      itinerary: 'Programma',
      meetingPoint: 'Ontmoetingspunt',
      reviews: 'Beoordelingen',
      faq: 'Veelgestelde vragen',
      highlights: 'Hoogtepunten',
      duration: 'Duur',
      groupSize: 'Groepsgrootte',
      smallGroupDefault: 'Kleine groep',
      pickupDefault: 'We bevestigen het exacte ophaalpunt en de voorwaarden na je boeking.',
      languages: 'Talen',
      difficulty: 'Moeilijkheidsgraad',
      minAge: 'Minimumleeftijd',
      cancellationPolicy: 'Annulering',
      whatToBring: 'Wat mee te nemen',
      selectDate: 'Kies datum',
      adults: 'Volwassenen',
      children: 'Kinderen',
      infants: "Baby's",
      total: 'Totaal',
      checkAvailability: 'Beschikbaarheid checken',
      relatedTitle: 'Misschien vind je dit ook leuk',
    },
    activitiesPage: {
      eyebrow: 'Activiteiten',
      headingPre: 'Alle ',
      headingEm: 'avonturen',
      subtitle: 'Vind je volgende ervaring uit meer dan 120 geselecteerde activiteiten.',
      filters: 'Filters',
      hide: 'Filters verbergen',
      searchPlaceholder: 'Zoeken…',
      destinations: 'Bestemmingen',
      categories: 'Categorieën',
      price: 'Prijs',
      resultsFound: 'activiteiten gevonden',
      perPage: 'Toon:',
      sort: 'Sorteren:',
      sortFeatured: 'Uitgelicht',
      sortPriceAsc: 'Prijs: laag naar hoog',
      sortPriceDesc: 'Prijs: hoog naar laag',
      sortRating: 'Best beoordeeld',
      viewList: 'Lijstweergave',
      viewGrid: 'Rasterweergave',
      loadError: 'We konden de activiteiten nu niet laden. Probeer het over een paar minuten opnieuw.',
      empty: 'Geen activiteiten met deze filters.',
    },
    activityDetail: {
      back: 'Terug naar activiteiten',
      notFound: 'Activiteit niet gevonden.',
      share: 'Activiteit delen',
      save: 'Activiteit opslaan',
      newActivity: 'Nieuwe activiteit op OWA',
      verified: 'Geverifieerde lokale aanbieder',
      onRequest: 'Boeking op aanvraag',
      prevImage: 'Vorige afbeelding',
      nextImage: 'Volgende afbeelding',
      tabDesc: 'Beschrijving',
      tabIncluded: 'Wat is inbegrepen',
      tabMeeting: 'Ontmoetingspunt',
      tabReviews: 'Beoordelingen',
      about: 'Over de ervaring',
      group: 'Groep',
      insurance: 'Verzekering',
      video: 'Bekijk de ervaring',
      cancelTitle: 'Annuleringsbeleid',
      requirements: 'Belangrijke vereisten',
      faqDuration: 'Hoe lang duurt deze activiteit?',
      faqIncluded: 'Wat is inbegrepen bij deze activiteit?',
      faqNotIncluded: 'Wat is niet inbegrepen?',
      faqMeetingPoint: 'Waar is het ontmoetingspunt?',
      faqCancellation: 'Wat is het annuleringsbeleid?',
      faqRequirements: 'Zijn er vereisten of beperkingen?',
      notIncludedH: 'Niet inbegrepen',
      whereMeet: 'Waar we elkaar ontmoeten',
      ratingOf5: 'van de 5',
      reviewsRefNote: 'referentiebeoordelingen. We publiceren eigen geverifieerde beoordelingen zodra er directe boekingen op OWA zijn.',
      newReviewsNote: 'We publiceren geverifieerde beoordelingen zodra de eerste boekingen zijn voltooid.',
      dateLabel: 'Datum',
      timeLabel: 'Tijd',
            couponPlaceholder: "Kortingscode",
            addonsTitle: "Optionele extra’s",
            addonPerPerson: "per persoon",
            couponApply: "Toepassen",
            couponDiscount: "Korting",
            couponInvalid: "Ongeldige coupon.",
            dateUnavailable: "Deze datum is niet beschikbaar.",
            soldOut: "Uitverkocht",
            seatsLeft: "{count} vrij",
      fullName: 'Volledige naam',
      phone: 'Telefoon',
      each: 'per stuk',
      acceptPolicy: 'Ik accepteer het beleid van deze activiteit:',
      payingSumup: 'Veilig betalen met SumUp',
      openingSumup: 'SumUp openen…',
      payPrep: 'Online betalen binnenkort',
      sendingRequest: 'Aanvraag verzenden…',
      requestBooking: 'Boeking aanvragen',
      confirmWhatsApp: 'Beschikbaarheid bevestigen via WhatsApp',
      whatsappDoubts: 'Vragen? Stuur ons een WhatsApp',
      payTotalEur: 'Volledige betaling in EUR',
      checkoutNoteReady: 'De checkout is al geïntegreerd. De veilige backend en echte inloggegevens moeten nog live.',
      checkoutNoteRequest: 'We nemen contact met je op om beschikbaarheid en definitieve voorwaarden te bevestigen.',
      amountEurNote: 'Het definitieve bedrag wordt in EUR verwerkt tegen de officiële prijs van de activiteit.',
      payNowDeposit: 'Nu betalen (aanbetaling)',
      balanceOnBoard: 'Restbedrag aan boord',
      tSelDateT: 'Kies een datum',
      tSelDateD: 'Kies de dag voor je activiteit.',
      tSelTimeT: 'Kies een tijd',
      tSelTimeD: 'Kies een van de beschikbare tijden.',
      tSelPaxT: 'Kies deelnemers',
      tSelPaxD: 'Voeg minstens één plaats toe voordat je verdergaat.',
      tDataT: 'Vul je gegevens in',
      tDataD: 'We hebben je naam, e-mail en telefoon nodig om de boeking te beheren.',
      tAcceptT: 'Accepteer de annulering',
      tAcceptD: 'Je moet het specifieke beleid van deze activiteit accepteren.',
      tSentT: 'Aanvraag verzonden',
      tSentD: 'We nemen zeer binnenkort contact met je op om beschikbaarheid en definitieve voorwaarden te bevestigen.',
      tSentFailT: 'Kon de aanvraag niet verzenden',
      tPayFailT: 'Kon de betaling niet starten',
      tryAgain: 'Probeer het over een paar minuten opnieuw.',
    },
    places: {
      eyebrow: 'Bestemmingen',
      headingPre: 'Canarische ',
      headingEm: 'Eilanden',
      subtitle: 'Acht vulkanische eilanden, gouden stranden, heldere luchten en eindeloze activiteiten.',
      loadError: 'We konden de bestemmingen nu niet laden. Probeer het over een paar minuten opnieuw.',
      empty: 'Nog geen bestemmingen gepubliceerd.',
      canaries: 'Canarische Eilanden',
      activitiesAvailable: 'activiteiten beschikbaar',
      comingSoon: 'Binnenkort',
    },
    notFound: {
      title: 'Pagina niet gevonden',
      description: 'De pagina die je zoekt bestaat niet of is verplaatst.',
      home: 'Terug naar home',
      activities: 'Bekijk activiteiten',

      islandNotFound: 'Eiland niet gevonden.',
      backDest: 'Alle bestemmingen',
      experiencesIn: 'Ervaringen op',
      viewCatalog: 'Bekijk in de catalogus',
      preparingTitle: 'We werken nog aan',
      preparingText: 'Er zijn nog geen ervaringen gepubliceerd op dit eiland. Vertel ons wat je zoekt en we stellen een persoonlijk voorstel samen.',
      whatsapp: 'Vraag via WhatsApp',
      contactUs: 'Neem contact op',
      catNotFound: 'Categorie niet gevonden.',
      backActivities: 'Alle activiteiten',
      experiences: 'ervaringen',
      soonTitle: 'Binnenkort meer ervaringen',
      soonText: 'We breiden deze categorie uit. Verken ondertussen alle activiteiten.',
      viewCatalogBtn: 'Bekijk de catalogus',
    },
    about: {
      eyebrow: 'Over OWA',
      headingPre: 'Avonturen ontworpen ',
      headingEm: 'voor ontdekkers',
      intro: 'OWA — Origin Wild Adventure ontstond in 2018 op Gran Canaria met een duidelijk doel: authentieke, duurzame en onvergetelijke ervaringen op de Canarische Eilanden bieden. Geen drukke tours. Alleen kleine groepen, lokale gidsen en de beste locaties.',
      yearsBig: '7+ jaar',
      yearsSmall: 'avonturen creëren',
      valuesEyebrow: 'Onze waarden',
      valuesHeadingPre: 'Wat ons ',
      valuesHeadingEm: 'drijft',
      v1Title: 'Authenticiteit',
      v1Text: 'Echte routes en ervaringen, weg van de massa.',
      v2Title: 'Passie',
      v2Text: 'Elk avontuur is met liefde voor detail gemaakt.',
      v3Title: 'Duurzaamheid',
      v3Text: 'We zorgen voor de eilanden voor de volgende generaties.',
      v4Title: 'Gemeenschap',
      v4Text: 'We werken met 100% lokale Canarische leveranciers.',
    },

    contact: {
      eyebrow: 'Contact',
      headingPre: 'We staan ',
      headingEm: 'voor je klaar',
      subtitle: 'Vertel ons welk avontuur je in gedachten hebt en we helpen je het waar te maken.',
      office: 'Hoofdkantoor',
      writeUs: 'Schrijf ons',
      formIntro: 'Vul het formulier in en ons team neemt zo snel mogelijk contact met je op.',
      name: 'Naam',
      subject: 'Onderwerp',
      messagePlaceholder: 'Vertel ons welk avontuur je interesseert…',
      send: 'Bericht versturen',
      sentTitle: 'Bericht verzonden!',
      sentText: 'We reageren binnen 24 uur.',
    },
    auth: {
      sessionExpired: "Je sessie is verlopen. Log opnieuw in.",
            orContinue: "Of ga verder met",
            continueFacebook: "Doorgaan met Facebook",
            loginEyebrow: 'Klantzone',
      loginTitle: 'Log in op je account',
      loginDesc: 'Log in om je boekingen te bekijken en je profiel te beheren.',
      password: 'Wachtwoord',
      forgot: 'Toegang herstellen',
      loggingIn: 'Bezig met inloggen…',
      login: 'Inloggen',
      noAccount: 'Nog geen account?',
      createAccount: 'Account aanmaken',
      loginError: 'Inloggen mislukt.',
      passwordPlaceholder: 'Je wachtwoord',
      benefitsTitle: 'Je OWA-account',
      benefit1: 'Bekijk en beheer je boekingen',
      benefit2: 'Bewaar je favoriete activiteiten',
      benefit3: 'Ontvang exclusieve aanbiedingen voor ontdekkers',
      regEyebrow: 'Nieuwe gebruiker',
      regTitle: 'Maak je account aan',
      regDesc: 'Registreer je om sneller te boeken en je favoriete avonturen te bewaren.',
      fullName: 'Volledige naam',
      fullNamePlaceholder: 'Voor- en achternaam',
      phone: 'Telefoon',
      passwordMin: 'Minstens 8 tekens',
      repeatPassword: 'Herhaal wachtwoord',
      repeatPasswordPlaceholder: 'Herhaal het wachtwoord',
      haveAccount: 'Ik heb al een account',
      creatingAccount: 'Account aanmaken…',
      passwordMismatch: 'De wachtwoorden komen niet overeen.',
      registerError: 'Account aanmaken mislukt.',
      accountEyebrow: 'Mijn account',
      noSessionTitle: 'Geen actieve sessie',
      noSessionDesc: 'Log in om je boekingen te bekijken en je profiel te beheren.',
      loginBtn: 'Inloggen',
      privateEyebrow: 'Privézone',
      hello: 'Hallo,',
      myAccount: 'Mijn account',
      accountDesc: 'Een overzicht van je profiel en je boekingen.',
      logout: 'Uitloggen',
      loggedOut: 'Succesvol uitgelogd.',
      profile: 'Profiel',
      profileSub: 'Gegevens voor het beheren van je boekingen.',
      role: 'Rol',
      company: 'Bedrijf',
      directCustomer: 'Directe klant',
      notProvided: 'Niet opgegeven',
      myBookings: 'Mijn boekingen',
      bookingsSub: 'Je geschiedenis en aankomende trips.',
      updating: 'Bijwerken',
      bookingsError: 'Kon de boekingen niet laden.',
      noBookingsTitle: 'Nog geen boekingen',
      noBookingsText: 'Zodra je je eerste boeking maakt, zie je hier je aankomende activiteiten, bedragen en statussen.',
      reference: 'Referentie',
      datePending: 'Datum volgt',
      amountPending: 'Bedrag volgt',
      stConfirmed: 'Bevestigd',
      stPaymentPending: 'Betaling in behandeling',
      stPaymentFailed: 'Betaling mislukt',
      stPending: 'In behandeling',
      stCancelled: 'Geannuleerd',
    },
    common: {
      loading: 'Laden…',
      error: 'Er ging iets mis.',
      retry: 'Opnieuw proberen',
      close: 'Sluiten',
      back: 'Terug',
      next: 'Volgende',
      previous: 'Vorige',
      showMore: 'Toon meer',
      showLess: 'Toon minder',
      needHelp: 'Kunnen we helpen?',
    },
    whatsapp: {
      label: 'Hulp nodig?',
      aria: 'Neem contact op via WhatsApp',
      prefill: 'Hallo OWA, ik wil graag informatie over een ervaring op de Canarische Eilanden.',
    },
  },
};

export default translations;
